import {
  pgTable,
  text,
  timestamp,
  boolean,
  integer,
  decimal,
  doublePrecision,
  uuid,
  pgEnum,
  jsonb,
  index,
  uniqueIndex,
  unique,
  check,
  bigint,
  primaryKey,
  customType,
} from 'drizzle-orm/pg-core';
import { relations, InferSelectModel, isNotNull, sql } from 'drizzle-orm';
import type { ModelPricing, ModelTransport } from '../models/catalog';
import type {
  CacheBilling,
  ListPriceSource,
  ProviderKind,
  RegistryAuditAction,
  RegistryCapabilities,
  RegistryDiscovery,
  RegistryLifecycle,
  RegistryPriceVersions,
  RegistryProbe,
  RegistrySource,
  RegistryStatus,
  RegistrySyncCounts,
  RegistrySyncOutcome,
  RegistrySyncTrigger,
  RegistryTier,
  RouteCostSource,
  RouteWire,
} from '../models/registry-types';

const tsvector = customType<{ data: string }>({
  dataType() { return 'tsvector'; },
});

// Postgres BYTEA mapped to Node Buffer / Uint8Array. Used by the
// per-item Yjs subdoc persistence on workspace_items (see migration
// 0024_canvas_yjs.sql) and by Hocuspocus's persistence callbacks.
const bytea = customType<{ data: Buffer; default: false }>({
  dataType() { return 'bytea'; },
  fromDriver(value: unknown): Buffer {
    if (Buffer.isBuffer(value)) return value;
    if (value instanceof Uint8Array) return Buffer.from(value);
    if (typeof value === 'string') return Buffer.from(value, 'binary');
    throw new Error('bytea: unexpected driver value');
  },
});

// Enums
export const accountTypeEnum = pgEnum('account_type', ['human', 'agent', 'external_contact', 'guest']);
export const voteTypeEnum = pgEnum('vote_type', ['up', 'down']);
// `illegal` = the reporter alleges the content is unlawful, which DSA Art. 16
// treats differently from a content-policy complaint. Added in migration 0053.
export const flagTypeEnum = pgEnum('flag_type', ['spam', 'harmful', 'low_quality', 'malicious', 'illegal']);
export const flagStatusEnum = pgEnum('flag_status', ['active', 'expired', 'rejected', 'upheld', 'suspended']);
export const mediaTypeEnum = pgEnum('media_type', ['image', 'video', 'gif', 'link', '3d', 'audio']);
export const channelTypeEnum = pgEnum('channel_type', ['category', 'channel', 'subchannel']);
export const tagTypeEnum = pgEnum('tag_type', ['user', 'system', 'dynamic']);
export const chatTypeEnum = pgEnum('chat_type', ['dm', 'group', 'system', 'delegate', 'delegate-ephemeral', 'delegate-manual']);

// Channel ownership and permissions enums
export const channelOwnerTypeEnum = pgEnum('channel_owner_type', ['platform', 'user', 'domain']);
export const channelVerificationStatusEnum = pgEnum('channel_verification_status', ['none', 'pending', 'verified', 'rejected']);
export const channelDefaultRoleEnum = pgEnum('channel_default_role', ['viewer', 'commenter', 'poster']);
export const channelRoleEnum = pgEnum('channel_role', ['owner', 'admin', 'moderator', 'poster', 'commenter', 'viewer']);
export const overrideTypeEnum = pgEnum('override_type', ['permanent', 'one_time', 'block']);
export const overrideSourceEnum = pgEnum('override_source', ['manual', 'approval_prompt', 'push_notification']);
export const domainVerificationMethodEnum = pgEnum('domain_verification_method', ['dns', 'meta', 'manual']);
export const domainVerificationStatusEnum = pgEnum('domain_verification_status', ['pending', 'verified', 'failed', 'expired']);
export const userRoleEnum = pgEnum('user_role', ['user', 'moderator', 'admin']);
export const referralCodeTypeEnum = pgEnum('referral_code_type', ['user', 'agent', 'channel', 'post', 'comment', 'deck', 'workspace', 'content']);
export const balanceTransactionTypeEnum = pgEnum('balance_transaction_type', [
  'deposit', 'withdrawal', 'token_lock', 'token_unlock',
  'charge_received', 'charge_sent', 'transfer', 'referral', 'subscription', 'token_topup', 'token_expired',
  'byok_llm', 'byok_mcp_sampling', 'commission_received', 'demo_grant',
  'attribution_payout',
  // Earnings-ledger types (migration 0047). Every mutation of users.earnings_balance is
  // identifiable by type alone — the A4/A8 reconciliation invariant depends on this:
  //   earnings_balance = Σ(attribution_payout + a2a_earning + creator_grant
  //                        + referral_earning + earnings_adjustment − withdrawal-typed debits)
  // 'a2a_earning'         : real-funded A2A recipient credit (the A2 retarget; replaces the
  //                         old totalBalance-crediting leg — historical charge_received rows
  //                         keep their historical meaning).
  // 'creator_grant'       : Creator Fund grant credited under ToS §21.12 (G7-amended).
  // 'earnings_adjustment' : clawback / correction under ToS §21.8 (signed; may drive the
  //                         balance negative by design — C5).
  // 'referral_earning'    : qualified referral reward credited as Creator Rewards
  //                         (migration 0051; historical 'referral' rows keep their
  //                         historical meaning — they credited total_balance as
  //                         promotional Service Credits). Refund/dispute reversals are
  //                         negative rows of this same type (may drive the balance
  //                         negative — that is the ToS 21.8 clawback, not drift).
  'a2a_earning', 'creator_grant', 'earnings_adjustment', 'referral_earning',
  // Observability + pool loop (migration 0063; observability-plan.md W2/W3/W5):
  // 'stripe_event'    : one row per processed Stripe webhook event on the
  //                     platform account; amount = -(Stripe fee), 0 when the
  //                     event carries no charge; key stripe:<event.id>; detail
  //                     {type,gross,net,tax} in metadata. INFORMATIONAL: never
  //                     mutates a user balance (see INFORMATIONAL_TX_TYPES).
  // 'pool_accrual'    : creator-pool loop on the pool system account, both
  //                     signs (credit = per-settle accrual carved from the
  //                     platform share + 55% of expiry rows; the one per-period
  //                     debit = distribution). Ledger-only: system accounts
  //                     never take per-settle users UPDATEs (hot-row rule).
  // 'purchase_expiry' : W3's monthly FIFO sweep of purchased credits past the
  //                     12-month term (counsel-gated; dedicated type so expiry
  //                     never pollutes deposit sums).
  'stripe_event', 'pool_accrual', 'purchase_expiry',
]);

/**
 * Ledger rows that mutate NO user balance: event markers and platform-level
 * journal facts. The W6 reconciliation (per-user Σ(ledger) = balance, per
 * bucket) excludes these BY TYPE, never by amount; analytics uses the same
 * list. Zero-amount 'referral' reputation markers and BYOK rows are excluded
 * by their own types' bucket mapping, not here.
 */
export const INFORMATIONAL_TX_TYPES = ['stripe_event', 'pool_accrual'] as const;
export const withdrawalRequestStatusEnum = pgEnum('withdrawal_request_status', [
  'requested', 'in_review', 'paid', 'rejected', 'canceled',
]);
export const paymentTokenStatusEnum = pgEnum('payment_token_status', ['active', 'expired', 'depleted', 'revoked']);
export const paymentLockStatusEnum = pgEnum('payment_lock_status', ['active', 'settled', 'released']);
export const contentTypeEnum = pgEnum('content_type', ['image', 'video', 'audio', 'document', 'model3d', 'folder', 'function', 'widget']);
export const contentVisibilityEnum = pgEnum('content_visibility', ['private', 'public', 'unlisted']);
// `editor` = explicit write grant (Reader/Editor/Owner workspace model). A
// plain `shared` link is read-only; `creator` owns + manages. `participant` is
// legacy (treated as read). See lib/content/permissions.ts canModifyContent.
// 'suggester' (migration 0085): read + propose tracked suggestions in an app
// that honours suggest mode (lib/collab/suggest-mode.ts); never write.
export const contentLinkRoleEnum = pgEnum('content_link_role', ['creator', 'editor', 'participant', 'shared', 'suggester']);
export const memoryGrantLevelEnum = pgEnum('memory_grant_level', ['search', 'read', 'readwrite']);

// Per-use attribution (project: per-use attribution) needs NO dedicated enums or
// tables: provenance is jsonb on widget_versions, signals ride user_interactions,
// and payouts are balance_transactions(type 'attribution_payout'). The marker-type
// / event-type / funding vocabularies live as TS unions in lib/attribution.

// (Workspace enums retired — see migration 0038. Workspace visibility is now
//  content_links visibility; item types live in the TS union WorkspaceItemType.)

// Users - @username for both humans and agents
export const users = pgTable('users', {
  id: uuid('id').defaultRandom().primaryKey(),
  username: text('username').unique().notNull(), // @username
  displayName: text('display_name').notNull(),
  email: text('email').unique(),
  avatarUrl: text('avatar_url'),
  bio: text('bio'),
  accountType: accountTypeEnum('account_type').default('human').notNull(),
  role: userRoleEnum('role').default('user').notNull(),
  agentUrl: text('agent_url'), // For agents: their API endpoint
  ownerId: uuid('owner_id'), // Human owner (nullable for external agents). Always set when known for agents/subagents.
  parentId: uuid('parent_id').references((): any => users.id), // Immediate parent: human owner (top-level) or agent parent (sub-agent)
  localName: text('local_name'), // Dot-namespace: the name segment owned by this entity (no dots). For flat names, equals username.
  
  // Reputation
  reputationScore: decimal('reputation_score', { precision: 10, scale: 2 }).default('0'),
  baseReputation: decimal('base_reputation', { precision: 10, scale: 2 }).default('0'),
  reputationVector: jsonb('reputation_vector'), // ℝ^384 TrustFlow embedding (computed by batch job)
  scalarReputation: decimal('scalar_reputation', { precision: 10, scale: 6 }).default('0'), // ||R[i]||₂ pre-norm magnitude
  profileEmbedding: jsonb('profile_embedding'), // ℝ^384 E5 embedding of agent bio/description (computed on profile update)
  humanLikesReceived: integer('human_likes_received').default(0),
  agentLikesReceived: integer('agent_likes_received').default(0),
  flagCount: integer('flag_count').default(0), // active human + system flags received (agent flags excluded)
  spamFlagCount: integer('spam_flag_count').default(0), // active human + system spam flags
  flagDampening: decimal('flag_dampening', { precision: 5, scale: 4 }).default('0'), // 0.0 to 1.0, computed by batch job
  suspendedAt: timestamp('suspended_at'), // set on admin suspension for batch lag mitigation
  deletedAt: timestamp('deleted_at'),
  deletedUsername: text('deleted_username'),

  // Legal — click-through (clickwrap) acceptance. `acceptedTerms*` track the
  // LATEST terms version re-affirmed at each login; `firstAcceptedTermsAt` is
  // set once at first acceptance and never overwritten (audit of first consent).
  firstAcceptedTermsAt: timestamp('first_accepted_terms_at'),
  acceptedTermsAt: timestamp('accepted_terms_at'),
  acceptedTermsVersion: text('accepted_terms_version'),

  // Stats
  postCount: integer('post_count').default(0),
  commentCount: integer('comment_count').default(0),
  followerCount: integer('follower_count').default(0),
  followingCount: integer('following_count').default(0),
  
  // Settings
  preferences: jsonb('preferences'),
  referralCode: text('referral_code').unique(),
  referredBy: uuid('referred_by'),
  
  // Balance (nanocredits, 10^9 scale). Full precision; BigInt serialization for drizzle-kit via scripts/bigint-json.cjs.
  totalBalance: bigint('total_balance', { mode: 'bigint' }).notNull().default(BigInt(0)),
  lockedBalance: bigint('locked_balance', { mode: 'bigint' }).notNull().default(BigInt(0)),
  planBalance: bigint('plan_balance', { mode: 'bigint' }).notNull().default(BigInt(0)),
  demoBalance: bigint('demo_balance', { mode: 'bigint' }).notNull().default(BigInt(0)), // Replenishable demo credits (separate from real balance)
  // Per-use attribution earnings (project: per-use attribution). A SEPARATE pool
  // so it is NOT clobbered by the absolute demoBalance set in grantDemoBalance.
  earningsBalance: bigint('earnings_balance', { mode: 'bigint' }).notNull().default(BigInt(0)),
  stripeCustomerId: text('stripe_customer_id').unique(),
  stripeSubscriptionId: text('stripe_subscription_id').unique(),
  stripeProductId: text('stripe_product_id'),
  planName: text('plan_name'),
  subscriptionStatus: text('subscription_status'),
  lastTokenCleanup: timestamp('last_token_cleanup'),
  // Guest lifecycle: last activity timestamp (debounced ~1/day). Inactivity GC
  // soft-deletes guests with `lastActiveAt < now - TTL`; re-engagement resets it.
  lastActiveAt: timestamp('last_active_at'),

  // Storage quota (bytes)
  storageUsed: bigint('storage_used', { mode: 'bigint' }).notNull().default(BigInt(0)),
  storageQuota: bigint('storage_quota', { mode: 'bigint' }).notNull().default(BigInt(104857600)), // 100MB default
  
  // User-level defaults applied to all agents unless overridden
  agentDefaults: jsonb('agent_defaults'), // { model?: string, integrationsEnabled?: boolean }
  allowByokSubstitution: boolean('allow_byok_substitution').default(false),
  
  // Visibility. For a person: their profile appears in discovery, search and
  // trending. For an agent: a stored copy of "acceptFrom holds everyone"
  // (lib/agents/visibility.ts), which makes it REACHABLE by anyone; being
  // listed in Discover is `listed` below (ADR 0046). The default is `true`
  // for people's sake, so every direct agent insert names it explicitly
  // (tests/unit/agents/agent-insert-is-public.test.ts, S-238).
  isPublic: boolean('is_public').default(true),
  // Agents only: the owner's "Publish in Discover" switch (ADR 0046,
  // migration 0089, lib/agents/listing.ts). Shown in Discover = public AND
  // listed. New agents are unlisted; people never read it.
  listed: boolean('listed').notNull().default(false),

  // Agent-only: reversed link to access token; active config (revisions)
  apiKeyId: uuid('api_key_id').references((): any => accessTokens.id),
  activeConfigId: uuid('active_config_id').references((): any => agentConfigs.id),
  
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  
  // Generated tsvector for FTS (populated by migration 0009)
  // C5 (migration 0047): admin freeze on the earnings economy. While set, the earnings
  // bucket is excluded from spend and withdrawal requests are blocked (ToS §21.8/§23).
  earningsFrozenAt: timestamp('earnings_frozen_at'),
  earningsFrozenReason: text('earnings_frozen_reason'),
  // F2 (migration 0047): Stripe Connect account for self-serve payouts (dark at launch).
  stripeConnectAccountId: text('stripe_connect_account_id'),
  // DEAD COLUMN — declared in 0000, never populated, never indexed, never
  // read; handle search is served by the trigram indexes from 0068. Kept
  // declared (and NOT dropped in 0068) deliberately: migrations run as an
  // ArgoCD PreSync job while the previous revision's pods are still serving,
  // and those pods enumerate every declared column in select().from(users) —
  // dropping it mid-rollout would break the running revision. Drop it in a
  // later cleanup migration once no pre-0068 image can run; that migration
  // should also `DROP TABLE IF EXISTS username_history` — an intermediate
  // (never-committed) 0068 draft created it and reached the DEV database
  // before the reservation feature was dropped, where it sits empty and
  // unreferenced.
  searchVector: tsvector('search_vector'),
}, (table) => ({
  usernameIdx: index('users_username_idx').on(table.username),
  emailIdx: index('users_email_idx').on(table.email),
  accountTypeIdx: index('users_account_type_idx').on(table.accountType),
  reputationIdx: index('users_reputation_idx').on(table.reputationScore),
  parentIdIdx: index('users_parent_id_idx').on(table.parentId),
  localNameIdx: index('users_local_name_idx').on(table.localName),
  deletedAtIdx: index('users_deleted_at_idx').on(table.deletedAt),
}));

// WebAuthn passkeys for biometric login
export const passkeys = pgTable('passkeys', {
  id: text('id').primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  publicKey: text('public_key').notNull(),
  counter: bigint('counter', { mode: 'number' }).notNull().default(0),
  deviceType: text('device_type'),
  backedUp: boolean('backed_up').default(false),
  transports: jsonb('transports'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  lastUsedAt: timestamp('last_used_at'),
}, (table) => ({
  userIdIdx: index('passkeys_user_id_idx').on(table.userId),
}));

// Agent config revisions (versioned config per agent user)
export const agentConfigs = pgTable('agent_configs', {
  id: uuid('id').defaultRandom().primaryKey(),
  agentId: uuid('agent_id').references(() => users.id).notNull(),
  version: integer('version').notNull(),
  description: text('description'),
  instructions: text('instructions'),
  avatarUrl: text('avatar_url'),
  // `is_public` is still a column on this table, but no longer in the schema
  // (2026-09-25, ADR 0046): it was a second copy of "acceptFrom holds
  // everyone" that no product code read. `users.is_public` is the one copy.
  // Dropped by a migration in a LATER release than this one, because pods of
  // the previous release still read it during the blue/green overlap.
  canTalkToOtherAgents: boolean('can_talk_to_other_agents').default(false),
  otherAgentsCanTalk: boolean('other_agents_can_talk').default(false),
  allowMcpSampling: boolean('allow_mcp_sampling').default(false),
  acceptFrom: jsonb('accept_from'), // Trust scope: who CAN call this agent (inbound). Array of patterns or {allow, deny}.
  talkTo: jsonb('talk_to'), // Trust scope: who this agent CAN call (outbound). Array of patterns or {allow, deny}.
  model: text('model'),
  intents: jsonb('intents'),
  skills: jsonb('skills'),
  greetingMessage: text('greeting_message'),
  greetingPrompt: text('greeting_prompt'),
  greetingImageMobile: text('greeting_image_mobile'),
  greetingImageDesktop: text('greeting_image_desktop'),
  suggestedActions: jsonb('suggested_actions'),
  enabledTools: jsonb('enabled_tools'),
  /**
   * User-authored functions on this agent. Name-keyed dictionary; consumed
   * by skills.cron / skills.custom_http / skills.custom_tools via `use:
   * <functionName>`. See `webagents/typescript/src/skills/functions/`.
   */
  functions: jsonb('functions'),
  /**
   * Per-agent function-execution limits — the middle layer between the
   * plan ceiling and per-function manifest hint. "Below-only" — the save
   * route rejects values above the plan ceiling with `upgrade_plan`.
   */
  functionLimits: jsonb('function_limits'),
  /**
   * Per-agent feature flags (selfEdit, functions, ...). Read by the
   * dispatcher / runtime / factory awareness renderer.
   */
  featureFlags: jsonb('feature_flags'),
  creditsPerToken: jsonb('credits_per_token'),
  minimumBalance: bigint('minimum_balance', { mode: 'bigint' }),
  commissionPercent: integer('commission_percent'),
  toolPricing: jsonb('tool_pricing'),
  // Content permissions
  canUploadContent: boolean('can_upload_content').default(false),
  canReadUserContent: boolean('can_read_user_content').default(false),
  maxStorageBytes: bigint('max_storage_bytes', { mode: 'bigint' }).default(BigInt(52428800)), // 50MB default
  backgroundChatId: uuid('background_chat_id'),
  changeMessage: text('change_message'),
  /**
   * Agent-level metadata bag (jsonb). Added in migration 0028 by
   * Plan v3-02 (browser control). First writer: Plan 2 stores
   *   metadata.{ provider, surfaceMedia, liveTransport,
   *              providerSessionTtlSeconds }
   * for browser-control system agents. Second writer (Plan v3-03):
   *   metadata.voice.*
   * The `functions` column above remains the tool-config source of
   * truth; this column is OPAQUE METADATA, not tool wiring.
   */
  metadata: jsonb('metadata').notNull().default(sql`'{}'::jsonb`),
  createdBy: uuid('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  agentIdx: index('agent_configs_agent_idx').on(table.agentId),
  versionIdx: uniqueIndex('agent_configs_agent_version_idx').on(table.agentId, table.version),
}));

// Agent generation templates (for batch agent creation)
export const agentTemplates = pgTable('agent_templates', {
  id: uuid('id').defaultRandom().primaryKey(),
  name: text('name').notNull(),
  description: text('description'),
  personalityTraits: text('personality_traits'), // JSON
  defaultTopics: text('default_topics'), // JSON
  postingStyle: text('posting_style'), // JSON
  biasSpectrum: text('bias_spectrum'), // JSON
  behaviorParams: text('behavior_params'), // JSON
  avatarStyle: text('avatar_style'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Agent generation batches (one per generation run)
export const agentBatches = pgTable('agent_batches', {
  id: uuid('id').defaultRandom().primaryKey(),
  templateId: uuid('template_id').references(() => agentTemplates.id).notNull(),
  batchSize: integer('batch_size').notNull(),
  generationParams: text('generation_params'), // JSON
  status: text('status').notNull().default('pending'), // pending | generating | complete | failed
  createdCount: integer('created_count').default(0),
  failedCount: integer('failed_count').default(0),
  completedAt: timestamp('completed_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  templateIdx: index('agent_batches_template_idx').on(table.templateId),
  statusIdx: index('agent_batches_status_idx').on(table.status),
}));

// Channel Categories (like Reddit's category groups)
export const channelCategories = pgTable('channel_categories', {
  id: uuid('id').defaultRandom().primaryKey(),
  name: text('name').notNull(),
  slug: text('slug').unique().notNull(),
  description: text('description'),
  iconEmoji: text('icon_emoji'),
  color: text('color'),
  sortOrder: integer('sort_order').default(0),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  slugIdx: index('channel_categories_slug_idx').on(table.slug),
}));

// Channels - //channel/subchannel/subsubchannel
export const channels = pgTable('channels', {
  id: uuid('id').defaultRandom().primaryKey(),
  name: text('name').notNull(),
  slug: text('slug').notNull(), // Full path: tech/programming/rust
  parentId: uuid('parent_id'), // For subchannels
  categoryId: uuid('category_id').references(() => channelCategories.id),
  channelType: channelTypeEnum('channel_type').default('channel').notNull(),
  
  description: text('description'),
  iconEmoji: text('icon_emoji'),
  logoUrl: text('logo_url'),
  bannerUrl: text('banner_url'),
  color: text('color'),
  rules: jsonb('rules'), // Channel-specific rules

  // Marketplace binding (app/agent channels under //apps and //agents). A
  // marketplace channel IS the app/agent's home; this points at the entity its
  // "Add to workspace" / "Chat" CTA targets. Exactly one is set for a listing
  // channel; both null for category/discussion channels.
  primaryContentId: uuid('primary_content_id').references((): any => content.id, { onDelete: 'set null' }), // app channel → widget content row
  primaryAgentId: uuid('primary_agent_id').references((): any => users.id, { onDelete: 'set null' }), // agent channel → agent user

  // Ownership fields
  ownerType: channelOwnerTypeEnum('owner_type').default('platform').notNull(),
  ownerId: uuid('owner_id').references(() => users.id), // user_id for user/domain, null for platform
  verificationStatus: channelVerificationStatusEnum('verification_status').default('none').notNull(),
  verifiedAt: timestamp('verified_at'),
  verificationMethod: text('verification_method'), // 'dns', 'meta', 'manual'
  
  // Permission defaults
  defaultRole: channelDefaultRoleEnum('default_role').default('poster').notNull(),
  allowSubchannelCreation: boolean('allow_subchannel_creation').default(false),
  
  // Stats
  postCount: integer('post_count').default(0),
  memberCount: integer('member_count').default(0),
  
  // Settings
  isNsfw: boolean('is_nsfw').default(false),
  isPrivate: boolean('is_private').default(false),
  requiresApproval: boolean('requires_approval').default(false),

  createdById: uuid('created_by_id').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  slugIdx: index('channels_slug_idx').on(table.slug),
  parentIdx: index('channels_parent_idx').on(table.parentId),
  categoryIdx: index('channels_category_idx').on(table.categoryId),
  ownerTypeIdx: index('channels_owner_type_idx').on(table.ownerType),
  ownerIdx: index('channels_owner_idx').on(table.ownerId),
}));

// Tags - #hashtags
export const tags = pgTable('tags', {
  id: uuid('id').defaultRandom().primaryKey(),
  name: text('name').unique().notNull(), // Without #, e.g., "trending"
  tagType: tagTypeEnum('tag_type').default('user').notNull(),
  description: text('description'),
  color: text('color'),
  
  // Stats
  useCount: integer('use_count').default(0),
  
  // For dynamic tags
  criteria: jsonb('criteria'), // Rules for auto-tagging
  
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  nameIdx: index('tags_name_idx').on(table.name),
  typeIdx: index('tags_type_idx').on(table.tagType),
  useCountIdx: index('tags_use_count_idx').on(table.useCount),
}));

// Posts
export const posts = pgTable('posts', {
  id: uuid('id').defaultRandom().primaryKey(),
  authorId: uuid('author_id').references(() => users.id).notNull(),
  channelId: uuid('channel_id').references(() => channels.id),
  
  title: text('title'),
  content: text('content'),
  
  // Media
  mediaType: mediaTypeEnum('media_type'),
  mediaUrl: text('media_url'),
  thumbnailUrl: text('thumbnail_url'),
  linkUrl: text('link_url'),
  linkMeta: jsonb('link_meta'), // OG metadata for links
  chatCta: text('chat_cta'),
  
  // Voting
  humanLikes: integer('human_likes').default(0),
  agentLikes: integer('agent_likes').default(0),
  humanDislikes: integer('human_dislikes').default(0),
  agentDislikes: integer('agent_dislikes').default(0),
  
  // Stats
  commentCount: integer('comment_count').default(0),
  viewCount: integer('view_count').default(0),
  shareCount: integer('share_count').default(0),
  repostCount: integer('repost_count').default(0),
  // Lifetime widget install counter. Incremented once per workspace_item
  // insert whose source is this widget post (iframe widget by ref_id or
  // native widget via state.cache.sourceWidgetPostId). Monotonic; no
  // decrement on removal. Not yet surfaced or sortable — collected for a
  // future "Most installed" ordering once usage stats exist.
  installCount: integer('install_count').notNull().default(0),
  // Denormalized count of direct remixes pointing at this post. Bumped
  // by `lib/widgets/remix.ts` on remix; not decremented when a child
  // widget is deleted (eventually-consistent). See ADR-v3-15.
  remixCount: integer('remix_count').notNull().default(0),
  // Per-post widget hints. Currently used by the publish pipeline as
  // the upsert key (`metadata.bundleFolderId`) for the
  // `posts_author_bundle_folder_uniq` partial unique index. See
  // migration 0027.
  metadata: jsonb('metadata').notNull().default(sql`'{}'::jsonb`),

  // Quality signals
  qualityScore: decimal('quality_score', { precision: 5, scale: 2 }).default('50'),
  
  // Quote repost
  quotedPostId: uuid('quoted_post_id').references((): any => posts.id, { onDelete: 'set null' }),

  // Flags
  isNsfw: boolean('is_nsfw').default(false),
  isPinned: boolean('is_pinned').default(false),
  isLocked: boolean('is_locked').default(false),

  // Full-text search vector (auto-generated from title + content)
  searchVector: tsvector('search_vector').generatedAlwaysAs(
    (): ReturnType<typeof sql> => sql`to_tsvector('simple', coalesce(${posts.title}, '') || ' ' || coalesce(${posts.content}, ''))`
  ),
  
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  authorCreatedIdx: index('posts_author_created_idx').on(table.authorId, table.createdAt),
  channelCreatedIdx: index('posts_channel_created_idx').on(table.channelId, table.createdAt),
  createdAtIdx: index('posts_created_at_idx').on(table.createdAt),
  scoreIdx: index('posts_score_idx').on(table.humanLikes),
  searchIdx: index('posts_search_idx').using('gin', table.searchVector),
  quotedPostIdx: index('posts_quoted_post_idx').on(table.quotedPostId),
  // Plan v3-05 — upsert key for the widget publish pipeline (idempotent
  // republish). Partial: only rows that ARE widget bundles. Created
  // CONCURRENTLY in migration 0027.
  authorBundleFolderUniq: uniqueIndex('posts_author_bundle_folder_uniq')
    .on(table.authorId, sql`(${table.metadata}->>'bundleFolderId')`)
    .where(sql`${table.metadata} ? 'bundleFolderId'`),
}));

// Post edit audit trail
export const postEdits = pgTable('post_edits', {
  id: uuid('id').defaultRandom().primaryKey(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }).notNull(),
  editorId: uuid('editor_id').references(() => users.id, { onDelete: 'set null' }),
  changes: jsonb('changes').notNull(), // { field: { from, to } } for each changed field
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  postIdx: index('post_edits_post_idx').on(table.postId),
  editorIdx: index('post_edits_editor_idx').on(table.editorId),
  createdAtIdx: index('post_edits_created_at_idx').on(table.createdAt),
}));

// Post subscriptions - user follows a post (replaces bookmarks). Following = saved + subscribed to comment updates + unread tracking.
export const postSubscriptions = pgTable('post_subscriptions', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }).notNull(),
  lastReadAt: timestamp('last_read_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userPostIdx: uniqueIndex('post_subs_user_post_idx').on(table.userId, table.postId),
  userIdx: index('post_subs_user_idx').on(table.userId),
}));

// Reposts - user/agent reposts a post (like X/Twitter RT)
export const reposts = pgTable('reposts', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('reposts_user_idx').on(table.userId),
  postIdx: index('reposts_post_idx').on(table.postId),
  userPostIdx: uniqueIndex('reposts_user_post_idx').on(table.userId, table.postId),
}));

// Pinned Posts - users can pin posts to their profile
export const pinnedPosts = pgTable('pinned_posts', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }).notNull(),
  pinnedAt: timestamp('pinned_at').defaultNow().notNull(),
}, (table) => ({
  userPostIdx: uniqueIndex('pinned_posts_user_post_idx').on(table.userId, table.postId),
  userIdx: index('pinned_posts_user_idx').on(table.userId, table.pinnedAt),
}));

// Saved Posts - private bookmarks
export const savedPosts = pgTable('saved_posts', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }).notNull(),
  savedAt: timestamp('saved_at').defaultNow().notNull(),
}, (table) => ({
  userPostIdx: uniqueIndex('saved_posts_user_post_idx').on(table.userId, table.postId),
  userIdx: index('saved_posts_user_idx').on(table.userId, table.savedAt),
}));

// Featured Content - admin-curated carousel, feed-pinned posts, and trending picks
export const featuredContent = pgTable('featured_content', {
  id: uuid('id').defaultRandom().primaryKey(),
  contentType: text('content_type').notNull(), // 'carousel_post' | 'featured_post' | 'trending_agent' | 'trending_channel' | 'featured_user' | 'featured_tag'
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }),
  targetUserId: uuid('target_user_id').references(() => users.id, { onDelete: 'cascade' }),
  targetChannelId: uuid('target_channel_id').references(() => channels.id, { onDelete: 'cascade' }),
  targetTagId: uuid('target_tag_id').references(() => tags.id, { onDelete: 'cascade' }),
  sortOrder: integer('sort_order').default(0).notNull(),
  displayMode: text('display_mode').default('always').notNull(), // 'always' | 'once'
  showBadge: boolean('show_badge').default(true).notNull(),
  createdBy: uuid('created_by').references(() => users.id).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  contentTypeIdx: index('featured_content_type_idx').on(table.contentType, table.sortOrder),
}));

// Starred Items - sidebar quick access pins
export const starredItems = pgTable('starred_items', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  itemType: text('item_type').notNull(),
  itemId: uuid('item_id').notNull(),
  starredAt: timestamp('starred_at').defaultNow().notNull(),
}, (table) => ({
  userItemIdx: uniqueIndex('starred_items_user_item_idx').on(table.userId, table.itemType, table.itemId),
  userIdx: index('starred_items_user_idx').on(table.userId),
}));

// Post Tags (many-to-many)
export const postTags = pgTable('post_tags', {
  id: uuid('id').defaultRandom().primaryKey(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }).notNull(),
  tagId: uuid('tag_id').references(() => tags.id, { onDelete: 'cascade' }).notNull(),
  addedById: uuid('added_by_id').references(() => users.id),
  isAutoGenerated: boolean('is_auto_generated').default(false),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  postIdx: index('post_tags_post_idx').on(table.postId),
  tagIdx: index('post_tags_tag_idx').on(table.tagId),
}));

// Channel Tags (many-to-many) - tags inherited by all posts in the channel
export const channelTags = pgTable('channel_tags', {
  id: uuid('id').defaultRandom().primaryKey(),
  channelId: uuid('channel_id').references(() => channels.id, { onDelete: 'cascade' }).notNull(),
  tagId: uuid('tag_id').references(() => tags.id, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  channelIdx: index('channel_tags_channel_idx').on(table.channelId),
  tagIdx: index('channel_tags_tag_idx').on(table.tagId),
  channelTagIdx: uniqueIndex('channel_tags_channel_tag_idx').on(table.channelId, table.tagId),
}));

// Comments
export const comments = pgTable('comments', {
  id: uuid('id').defaultRandom().primaryKey(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }).notNull(),
  authorId: uuid('author_id').references(() => users.id).notNull(),
  parentId: uuid('parent_id'), // For nested comments
  
  content: text('content').notNull(),
  
  // Voting
  humanLikes: integer('human_likes').default(0),
  agentLikes: integer('agent_likes').default(0),
  humanDislikes: integer('human_dislikes').default(0),
  agentDislikes: integer('agent_dislikes').default(0),
  
  // Quality
  qualityScore: decimal('quality_score', { precision: 5, scale: 2 }).default('50'),

  // Flags
  isNsfw: boolean('is_nsfw').default(false),
  
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  postCreatedIdx: index('comments_post_created_idx').on(table.postId, table.createdAt),
  authorIdx: index('comments_author_idx').on(table.authorId),
  parentIdx: index('comments_parent_idx').on(table.parentId),
}));

// Votes
export const votes = pgTable('votes', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  postId: uuid('post_id').references(() => posts.id, { onDelete: 'cascade' }),
  commentId: uuid('comment_id').references(() => comments.id, { onDelete: 'cascade' }),
  voteType: voteTypeEnum('vote_type').notNull(),
  weight: decimal('weight', { precision: 3, scale: 2 }).default('1'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userPostIdx: index('votes_user_post_idx').on(table.userId, table.postId),
  userCommentIdx: index('votes_user_comment_idx').on(table.userId, table.commentId),
}));

// Content Flags (moderation/trust signals, separate from votes)
export const contentFlags = pgTable('content_flags', {
  id: uuid('id').defaultRandom().primaryKey(),
  reporterId: uuid('reporter_id').references(() => users.id).notNull(),
  reporterAccountType: text('reporter_account_type').notNull(), // 'human' | 'agent' | 'system'
  targetType: text('target_type').notNull(), // 'post' | 'comment' | 'agent' | 'user'
  targetId: uuid('target_id').notNull(),
  flagType: flagTypeEnum('flag_type').notNull(),
  status: flagStatusEnum('status').default('active').notNull(),
  reason: text('reason'),
  evidence: jsonb('evidence'), // NLI response signature evidence for agent flags
  priority: integer('priority').notNull().default(3), // 0=P0, 1=P1, 2=P2, 3=P3
  expiresAt: timestamp('expires_at'), // default 90 days, NULL for admin-upheld
  reviewedAt: timestamp('reviewed_at'),
  reviewedBy: uuid('reviewed_by').references(() => users.id),
  // Review outcome fields (0050, DSA Article 17). `status` is the verdict on
  // the FLAG (upheld/rejected); `resolution` is the action taken on the TARGET
  // ('content_removed' | 'listing_unlisted' | 'account_suspended' | 'none',
  // CHECK-constrained in SQL). `provision` cites the ToS/policy clause;
  // `sorSentAt` stamps statement-of-reasons delivery (email idempotency key
  // mod-sor:<flagId> makes the email_messages row the receipt).
  resolution: text('resolution'),
  reviewNotes: text('review_notes'),
  provision: text('provision'),
  sorSentAt: timestamp('sor_sent_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  targetIdx: index('content_flags_target_idx').on(table.targetType, table.targetId),
  reporterIdx: index('content_flags_reporter_idx').on(table.reporterId, table.createdAt),
  queueIdx: index('content_flags_queue_idx').on(table.priority, table.createdAt),
  reporterTargetUniq: uniqueIndex('content_flags_reporter_target_uniq').on(
    table.reporterId, table.targetType, table.targetId, table.flagType
  ),
}));

// Interaction Ratings (per-message satisfaction signal)
export const interactionRatings = pgTable('interaction_ratings', {
  id: uuid('id').defaultRandom().primaryKey(),
  messageId: uuid('message_id').references(() => messages.id).notNull(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  agentId: uuid('agent_id').references(() => users.id).notNull(),
  rating: integer('rating').notNull(), // -1 or 1
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  messageUserUniq: uniqueIndex('interaction_ratings_message_user_uniq').on(table.messageId, table.userId),
  agentIdx: index('interaction_ratings_agent_idx').on(table.agentId),
  createdAtIdx: index('interaction_ratings_created_idx').on(table.createdAt),
}));

// Agent Interactions (NLI call tracking for reputation graph edges)
export const agentInteractions = pgTable('agent_interactions', {
  id: uuid('id').defaultRandom().primaryKey(),
  callerAgentId: uuid('caller_agent_id').references(() => users.id).notNull(),
  calleeAgentId: uuid('callee_agent_id').references(() => users.id).notNull(),
  status: text('status').notNull().default('success'), // 'success' | 'error' | 'flagged'
  queryText: text('query_text'), // raw query for batch domain classification
  contentEmbedding: jsonb('content_embedding'), // E5 384-dim embedding of queryText (async via BullMQ)
  tokenId: uuid('token_id').references(() => paymentTokens.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  callerIdx: index('agent_interactions_caller_idx').on(table.callerAgentId),
  calleeIdx: index('agent_interactions_callee_idx').on(table.calleeAgentId),
  pairIdx: index('agent_interactions_pair_idx').on(table.callerAgentId, table.calleeAgentId),
  createdAtIdx: index('agent_interactions_created_idx').on(table.createdAt),
}));

// Agent Reputation Domains (per-domain reputation vectors, computed by batch job)
export const agentReputationDomains = pgTable('agent_reputation_domains', {
  id: uuid('id').defaultRandom().primaryKey(),
  agentId: uuid('agent_id').references(() => users.id).notNull(),
  domainId: text('domain_id').notNull(), // references DOMAIN_REGISTRY id
  score: decimal('score', { precision: 10, scale: 6 }).notNull().default('0'),
  source: text('source').notNull().default('interaction'), // 'interaction' | 'bootstrap' | 'content' | 'content_authority' | 'external'
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  agentDomainUniq: uniqueIndex('agent_rep_domains_agent_domain_uniq').on(table.agentId, table.domainId, table.source),
  agentIdx: index('agent_rep_domains_agent_idx').on(table.agentId),
}));

// Follows
export const follows = pgTable('follows', {
  id: uuid('id').defaultRandom().primaryKey(),
  followerId: uuid('follower_id').references(() => users.id).notNull(),
  followingId: uuid('following_id').references(() => users.id).notNull(),
  lastSeenPostAt: timestamp('last_seen_post_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  followerIdx: index('follows_follower_idx').on(table.followerId),
  followingIdx: index('follows_following_idx').on(table.followingId),
}));

// Channel Memberships
export const channelMembers = pgTable('channel_members', {
  id: uuid('id').defaultRandom().primaryKey(),
  channelId: uuid('channel_id').references(() => channels.id, { onDelete: 'cascade' }).notNull(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  role: text('role').default('member'), // member, moderator, admin
  lastReadAt: timestamp('last_read_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  channelIdx: index('channel_members_channel_idx').on(table.channelId),
  userIdx: index('channel_members_user_idx').on(table.userId),
}));

// Channel Permissions - granular role-based access control
export const channelPermissions = pgTable('channel_permissions', {
  id: uuid('id').defaultRandom().primaryKey(),
  channelId: uuid('channel_id').references(() => channels.id, { onDelete: 'cascade' }).notNull(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  role: channelRoleEnum('role').notNull(),
  grantedBy: uuid('granted_by').references(() => users.id),
  grantedAt: timestamp('granted_at').defaultNow().notNull(),
  expiresAt: timestamp('expires_at'), // Optional, for temp bans
}, (table) => ({
  channelIdx: index('channel_permissions_channel_idx').on(table.channelId),
  userIdx: index('channel_permissions_user_idx').on(table.userId),
  channelUserIdx: uniqueIndex('channel_permissions_channel_user_idx').on(table.channelId, table.userId),
}));

// Reserved Prefixes - block certain channel name prefixes
export const reservedPrefixes = pgTable('reserved_prefixes', {
  id: uuid('id').defaultRandom().primaryKey(),
  prefix: text('prefix').unique().notNull(), // 'com', 'org', '@', etc.
  reason: text('reason'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  prefixIdx: index('reserved_prefixes_prefix_idx').on(table.prefix),
}));

// Domain Verifications - for domain-owned channels
export const domainVerifications = pgTable('domain_verifications', {
  id: uuid('id').defaultRandom().primaryKey(),
  domain: text('domain').notNull(), // 'google.com'
  userId: uuid('user_id').references(() => users.id).notNull(), // who initiated
  channelId: uuid('channel_id').references(() => channels.id), // resulting channel
  method: domainVerificationMethodEnum('method').notNull(),
  verificationCode: text('verification_code').notNull(),
  status: domainVerificationStatusEnum('status').default('pending').notNull(),
  verifiedAt: timestamp('verified_at'),
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  domainIdx: index('domain_verifications_domain_idx').on(table.domain),
  userIdx: index('domain_verifications_user_idx').on(table.userId),
  statusIdx: index('domain_verifications_status_idx').on(table.status),
  // ONE VERIFIED ROW PER DOMAIN (migration 0086, app-hosts plan A5,
  // 2026-09-24). Routing, `frame-ancestors` and the sign-in hand-off all
  // `findFirst` a verified row by domain; without this two accounts could
  // both verify one name and which one answered came down to row order.
  // Partial on `status = 'verified'` so pending and expired claims, which
  // are plentiful and harmless, stay unconstrained. Built CONCURRENTLY by
  // the runner (`-- migrate:concurrently`).
  verifiedDomainUidx: uniqueIndex('domain_verifications_verified_domain_uidx')
    .on(table.domain)
    .where(sql`${table.status} = 'verified'`),
}));

/**
 * Which app answers which path on a verified custom domain.
 *
 *   mydomain.com/        -> app A
 *   mydomain.com/shop    -> app B
 *
 * Keyed on the VERIFICATION row rather than the domain string, so a binding
 * cannot outlive the ownership proof that justifies it: unverifying or deleting
 * the domain cascades the bindings away. `path` is stored normalised (leading
 * slash, no trailing slash, `'/'` for the root) so the unique index is a real
 * constraint rather than a suggestion — `/shop` and `/shop/` must not both bind.
 *
 * Note this table only covers the app UI. An app's fn-backed HTTP endpoints are
 * NOT listed here: they are derived at resolve time from the app's dedicated
 * agent (`getHttpEndpoints()`), because the app's own manifest is the source of
 * truth for those and hand-entering them would drift.
 */
export const domainAppBindings = pgTable('domain_app_bindings', {
  id: uuid('id').defaultRandom().primaryKey(),
  verificationId: uuid('verification_id')
    .references(() => domainVerifications.id, { onDelete: 'cascade' })
    .notNull(),
  path: text('path').notNull(),
  contentId: uuid('content_id')
    .references(() => content.id, { onDelete: 'cascade' })
    .notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  // One app per path per domain. The whole routing model rests on this.
  verificationPathIdx: uniqueIndex('domain_app_bindings_verification_path_idx')
    .on(table.verificationId, table.path),
  contentIdx: index('domain_app_bindings_content_idx').on(table.contentId),
}));

// TLDs - IANA root zone database for TLD blocking
export const tlds = pgTable('tlds', {
  tld: text('tld').primaryKey(), // 'com', 'org', 'ai', etc.
  type: text('type').notNull(), // 'generic', 'country-code', 'sponsored', 'infrastructure'
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Channel Aliases - short slugs pointing to canonical channels
export const channelAliases = pgTable('channel_aliases', {
  alias: text('alias').primaryKey(), // 'san-francisco', 'tokyo'
  channelId: uuid('channel_id').references(() => channels.id, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  channelIdx: index('channel_aliases_channel_idx').on(table.channelId),
}));

// User Tags (for personalized dynamic tags)
export const userTags = pgTable('user_tags', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  tagId: uuid('tag_id').references(() => tags.id).notNull(),
  score: decimal('score', { precision: 5, scale: 2 }).default('0'), // Relevance score
  source: text('source'), // 'likes', 'posts', 'follows', 'marketing'
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('user_tags_user_idx').on(table.userId),
  tagIdx: index('user_tags_tag_idx').on(table.tagId),
}));

// Referral Codes - enhanced referral tracking for content attribution
export const referralCodes = pgTable('referral_codes', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(), // Owner of the code (human user)
  code: text('code').unique().notNull(), // 8-char alphanumeric code
  type: referralCodeTypeEnum('type').default('user').notNull(), // user, agent, channel, post, comment, deck
  sourceId: uuid('source_id'), // agent_id, channel_id, post_id, or comment_id depending on type
  // Free-form admin annotation (e.g. "Sequoia / Doug" on a deck-typed share). Null otherwise.
  label: text('label'),
  // Optional time-box for any code; null = never expires.
  expiresAt: timestamp('expires_at'),
  // Per-row metadata bag. For type='deck' rows: { deckSlug?: string, allowAll?: boolean }.
  // jsonb so callers can store typed substructures per code-type without
  // schema changes.
  metadata: jsonb('metadata').$type<Record<string, unknown> | null>(),
  referralCount: integer('referral_count').default(0),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('referral_codes_user_idx').on(table.userId),
  codeIdx: uniqueIndex('referral_codes_code_idx').on(table.code),
  typeSourceIdx: index('referral_codes_type_source_idx').on(table.type, table.sourceId),
}));

// Referrals - tracks direct referral relationships only.
// Multilevel rewards are computed at qualification time by walking
// the users.referredBy chain (no denormalized level column needed).
export const referrals = pgTable('referrals', {
  id: uuid('id').defaultRandom().primaryKey(),
  referrerId: uuid('referrer_id').references(() => users.id).notNull(),
  referredId: uuid('referred_id').references(() => users.id).notNull(),
  referralCodeId: uuid('referral_code_id').references(() => referralCodes.id),
  rewardGranted: boolean('reward_granted').default(false),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  referrerIdx: index('referrals_referrer_idx').on(table.referrerId),
  codeIdx: index('referrals_code_idx').on(table.referralCodeId),
}));

// Deck Shares - admin-minted referral codes for VC deck access
// Recipients are identified by short code; tracking attributes via rb_anon_id
// cookie which stitches pre/post-signup behaviour in user_interactions.
export const deckShares = pgTable('deck_shares', {
  id: uuid('id').defaultRandom().primaryKey(),
  code: text('code').unique().notNull(), // 8-char alphanumeric (generateReferralCode)
  deckSlug: text('deck_slug').notNull(),
  recipientEmail: text('recipient_email').notNull(),
  recipientLabel: text('recipient_label'), // e.g. "Acme Ventures"
  createdBy: uuid('created_by').references(() => users.id).notNull(),
  expiresAt: timestamp('expires_at'), // null = never expires
  revokedAt: timestamp('revoked_at'),
  firstViewedAt: timestamp('first_viewed_at'),
  lastViewedAt: timestamp('last_viewed_at'),
  viewCount: integer('view_count').default(0).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  codeIdx: uniqueIndex('deck_shares_code_idx').on(table.code),
  slugIdx: index('deck_shares_slug_idx').on(table.deckSlug),
  emailIdx: index('deck_shares_email_idx').on(table.recipientEmail),
  createdByIdx: index('deck_shares_created_by_idx').on(table.createdBy),
}));

// Agent Registrations (for AOAuth)
export const agentRegistrations = pgTable('agent_registrations', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  agentUrl: text('agent_url').unique().notNull(),
  clientId: text('client_id').unique().notNull(),
  clientSecret: text('client_secret').notNull(),
  scopes: jsonb('scopes'),
  // SPKI PEM of the signing key of a registration made BEFORE migration 0083
  // (ADR 0038 step 5), and nothing else. The key set lives in
  // `agent_registration_keys` below, and nothing reads or writes this column:
  // no image writes it (operator decision 2026-09-19: no rollback copy), and
  // such a registration takes the key admission rule with no key held like
  // any other. Until 2026-09-19 a one-time bridge read it to turn that PEM
  // into the registration's first key row (S-179 was that bridge's hole);
  // removed when the operator ruled that no back-compat is owed. Dropped by
  // the contract migration (W2 design section 5.4). Nullable, and NULL on
  // every registration made since.
  publicKey: text('public_key'),
  isActive: boolean('is_active').default(true),
  lastSeenAt: timestamp('last_seen_at'),
  isOnline: boolean('is_online').default(false),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  agentUrlIdx: index('agent_registrations_agent_url_idx').on(table.agentUrl),
  clientIdIdx: index('agent_registrations_client_id_idx').on(table.clientId),
}));

/* THE SIGNING KEYS OF AN EXTERNALLY HOSTED AGENT, one row per key, keyed by the
   RFC 7638 JWK thumbprint (migration 0083, ADR 0038 step 5, W2 design section 5).
   Until this table `agent_registrations.public_key` held exactly ONE PEM per
   agent, so a key could not be rotated without a hard cutover; a set with
   overlap is what rotation needs. Removal is `removed_at` (the row stays for
   the audit trail); a key belongs to exactly one ACTIVE registration, which is
   what key-anchored continuity means, hence the partial unique index. The
   per-registration index is plain, not partial (2026-09-19): the ON DELETE
   CASCADE needs the removed rows too. (The S-179 "has this registration EVER
   held a row" read that also wanted it went with the PEM bridge the same
   day.) No `kty` column (trimmed the same day): it was always 'OKP' and
   repeated `jwk.kty`. Only `lib/auth/web-bot-auth/keys.ts` reads or writes
   this table. */
export const agentRegistrationKeys = pgTable('agent_registration_keys', {
  id: uuid('id').defaultRandom().primaryKey(),
  registrationId: uuid('registration_id').references(() => agentRegistrations.id, { onDelete: 'cascade' }).notNull(),
  // RFC 7638 SHA-256 thumbprint, base64url, no padding: 43 characters.
  thumbprint: text('thumbprint').notNull(),
  // Public members only ({kty, crv, x} for Ed25519). Never a private member.
  jwk: jsonb('jwk').notNull(),
  // 'registration' | 'rotation' | 'owner': how the key was admitted (`KeySource`).
  source: text('source').notNull(),
  // The already-held thumbprint that co-signed the request that admitted this
  // key, or null. Written under every admission mode so the continuity switch
  // (`AOAUTH_KEY_ADMISSION_MODE`) can be flipped later with no schema change.
  coSignedBy: text('co_signed_by'),
  firstSeenAt: timestamp('first_seen_at').defaultNow().notNull(),
  // Coarse: the verifier rewrites it at most once an hour per key (2026-09-19).
  lastUsedAt: timestamp('last_used_at'),
  // When the published set was last seen to carry this key (design 4.4).
  refreshedAt: timestamp('refreshed_at').defaultNow().notNull(),
  // Set when the published set no longer carries it. Never verified again.
  removedAt: timestamp('removed_at'),
}, (table) => ({
  thumbprintActiveUidx: uniqueIndex('agent_registration_keys_thumbprint_active_uidx')
    .on(table.thumbprint)
    .where(sql`${table.removedAt} IS NULL`),
  registrationIdx: index('agent_registration_keys_registration_idx').on(table.registrationId),
}));

// Unified access tokens - replaces api_keys + oauth_tokens
// Types: 'api_key' (platform JWT), 'oauth_app' (OAuth consent), 'agent' (agent key), 'provider' (BYOK)
export const accessTokens = pgTable('access_tokens', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),

  // Identity
  type: text('type').notNull(),                      // 'api_key' | 'oauth_app' | 'agent' | 'provider'
  name: text('name').notNull(),                      // "My CLI key", "Claude", "fundraiser", "OpenAI"
  jti: text('jti').unique(),                         // JWT ID (null for provider keys)
  keyPrefix: text('key_prefix'),                     // display prefix "eyJh..." or "sk-pr..."

  // OAuth-specific (type = 'oauth_app')
  clientId: text('client_id'),                       // linked OAuth client
  refreshToken: text('refresh_token').unique(),
  scopes: text('scopes').array().default(['read', 'write']),

  // Agent-specific (type = 'agent')
  agentId: uuid('agent_id').references(() => users.id),

  // Provider-specific (type = 'provider')
  provider: text('provider').notNull().default('robutler'), // 'robutler' | 'openai' | 'anthropic' | ...
  encryptedKey: text('encrypted_key'),               // AES-256 encrypted third-party key

  // Spending limits (nanocredits) -- UNIFORM for ALL types
  // Every token gets defaults; user can adjust or remove.
  // LABELS CORRECTED 2026-09-22 (S-208): these read $10 / $100 / $1, which
  // would hold only if a credit were 1e11 nanocredits. It is 1e9
  // (`lib/balance/units.ts:9`, `CREDITS_PER_USD = 1`), so each column default
  // is a hundred times the amount its comment claimed. The VALUES are
  // unchanged; retuning them is an operator decision, not a comment fix.
  // These column defaults also differ from `DEFAULT_LIMITS` in
  // lib/access-tokens/service.ts, which is what `createAccessToken` writes:
  // a row minted through that path gets 100/500/10 credits, a row inserted
  // without it gets the 1000/10000/100 below.
  limitDaily: bigint('limit_daily', { mode: 'bigint' }).default(BigInt(1000000000000)),    // 1000 credits/day
  limitTotal: bigint('limit_total', { mode: 'bigint' }).default(BigInt(10000000000000)),   // 10000 credits total
  limitPerUse: bigint('limit_per_use', { mode: 'bigint' }).default(BigInt(100000000000)),  // 100 credits/use
  spentToday: bigint('spent_today', { mode: 'bigint' }).notNull().default(BigInt(0)),
  spentTotal: bigint('spent_total', { mode: 'bigint' }).notNull().default(BigInt(0)),
  spentDate: text('spent_date'),

  // Status
  isActive: boolean('is_active').notNull().default(true),
  expiresAt: timestamp('expires_at'),
  lastUsedAt: timestamp('last_used_at'),
  revokedAt: timestamp('revoked_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('access_tokens_user_idx').on(table.userId),
  jtiIdx: index('access_tokens_jti_idx').on(table.jti),
  typeIdx: index('access_tokens_type_idx').on(table.type),
  providerIdx: index('access_tokens_provider_idx').on(table.provider),
  clientIdx: index('access_tokens_client_idx').on(table.clientId),
  agentIdx: index('access_tokens_agent_idx').on(table.agentId),
  // Bearer-token validation filters active tokens per user on every
  // token-authed API call (R6; built CONCURRENTLY by migration 0054).
  activeUserIdx: index('access_tokens_active_user_idx').on(table.isActive, table.userId),
}));

// MCP Connections
export const mcpConnections = pgTable('mcp_connections', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  provider: text('provider').notNull(), // 'chatgpt', 'claude', etc.
  connectionToken: text('connection_token').unique().notNull(),
  accessTokenId: uuid('access_token_id').references(() => accessTokens.id),
  isActive: boolean('is_active').default(true),
  lastUsedAt: timestamp('last_used_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('mcp_connections_user_idx').on(table.userId),
  tokenIdx: index('mcp_connections_token_idx').on(table.connectionToken),
}));

// Balance transactions ledger
export const balanceTransactions = pgTable('balance_transactions', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  amount: bigint('amount', { mode: 'bigint' }).notNull(),
  type: balanceTransactionTypeEnum('type').notNull(),
  relatedTokenId: uuid('related_token_id'),
  relatedChargeId: uuid('related_charge_id'),
  description: text('description'),
  idempotencyKey: text('idempotency_key'),
  // Self-description (migration 0063), all nullable: pre-migration rows read
  // as unclassified. flow/balanceSource values are authoritative in
  // lib/db/ledger-meta.ts; metadata carries typed detail (Stripe fee capture,
  // expiry provenance), never parsed out of description.
  flow: text('flow'),
  balanceSource: text('balance_source'),
  metadata: jsonb('metadata'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('balance_transactions_user_idx').on(table.userId),
  idempotencyKeyIdx: uniqueIndex('balance_transactions_idempotency_key_idx').on(table.idempotencyKey).where(sql`idempotency_key IS NOT NULL`),
  // A8 (migration 0047): the earnings UI and monthly statements group a user's rows by
  // type and day; without this the queries scan the user's whole ledger history.
  userTypeCreatedIdx: index('balance_transactions_user_type_created_idx').on(
    table.userId, table.type, table.createdAt,
  ),
  // Platform-wide sums (pool true-up, fee reconciliation, finance rollup)
  // group by type over time windows; the index above is user-scoped
  // (migration 0063).
  typeCreatedIdx: index('balance_transactions_type_created_idx').on(
    table.type, table.createdAt,
  ),
  // OWED, not built (operator decision 2026-09-19): the anonymous per-call
  // sale's time index, `balance_transactions_machine_sale_idx ON (created_at)
  // WHERE flow = 'machine_sale'`, built CONCURRENTLY, lands with the first
  // surface wired to the anonymous door. Until it exists every sale reader
  // answers not ready (lib/payments/mpp/sale-ledger-index.ts).
}));

// ── Launch money foundations (migration 0047) ────────────────────────────────

// H1: audited runtime settings. Product decisions live here as config (spend order,
// attribution budget, purchases-per-market, conversion basis, cash-out date, kill
// switch, launch gate) so changing one is a flip, not a deploy. Read through
// lib/platform/settings.ts, which falls back to env defaults when a key (or this
// table, pre-migration) is absent. Never read this table directly from features.
export const platformSettings = pgTable('platform_settings', {
  key: text('key').primaryKey(),
  value: jsonb('value').notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  updatedBy: uuid('updated_by').references(() => users.id),
});

// Append-only audit for every settings write (who, when, old, new). H1's contract:
// funding the pool, flipping a market, announcing the cash-out date all leave a row.
export const platformSettingsAudit = pgTable('platform_settings_audit', {
  id: uuid('id').defaultRandom().primaryKey(),
  key: text('key').notNull(),
  oldValue: jsonb('old_value'),
  newValue: jsonb('new_value').notNull(),
  changedBy: uuid('changed_by').references(() => users.id),
  note: text('note'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  keyIdx: index('platform_settings_audit_key_idx').on(table.key, table.createdAt),
}));

// ── LLM model registry (migration 0095, model-registry-and-admin-plan.md, rewritten 2026-10-06) ──
//
// Models, providers and routes as data. A MODEL is what a user picks and pays
// for (its `list_price` is the retail basis; retail = list x
// MODEL_RETAIL_MULTIPLIER, computed at read time and never stored). A
// PROVIDER is who the platform pays, with two switches (`listed`: its home
// models may be offered; `routing_enabled`: its routes may serve). A ROUTE is
// one model on one provider in that model's ordered fallback chain, with the
// provider's own cost. The static catalog (lib/models/catalog.ts) plus
// BEDROCK_ROUTES seed these tables (insert only) and remain the whole
// behaviour while `models.registry.enabled` is off; once on, a row is the
// truth for the model it names. Read through lib/models/registry.ts (an
// in-memory snapshot, never a per-request query); written by the seed
// (lib/models/registry-seed.ts), the sync and the admin API only. Nothing
// reads them while the setting is off.

/**
 * Who the platform pays. `key` is the catalog provider key and the id prefix
 * (`openai`, `bedrock`, `local`, ...), never renamed: it is a wire value.
 * Keys are secrets in the environment, never here; `key_source` only names
 * the variable (`minted` for Bedrock, `none` for local).
 */
export const llmProviders = pgTable('llm_providers', {
  key: text('key').primaryKey(),
  label: text('label').notNull(),
  kind: text('kind').$type<ProviderKind>().notNull(),
  keySource: text('key_source'),
  /** This provider's HOME models may be offered. */
  listed: boolean('listed').default(true).notNull(),
  /** Its routes may serve, for any model. */
  routingEnabled: boolean('routing_enabled').default(true).notNull(),
  /** Null means the adapter's default; set only for a provider whose default moved. */
  baseUrl: text('base_url'),
  region: text('region'),
  note: text('note'),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  updatedBy: uuid('updated_by').references(() => users.id, { onDelete: 'set null' }),
}, (table) => ({
  kindCheck: check('llm_providers_kind_check', sql`${table.kind} IN ('direct', 'bedrock', 'local', 'mock')`),
}));

/**
 * One row per model the registry knows. `model_id` is the catalog id, frozen
 * (`bedrock/<slug>` for discovered rows); `provider` is the HOME provider,
 * the id prefix, whose adapter family and tool semantics the model keeps on
 * every route. Rows are never deleted: an agent pinned to a model that later
 * goes away must always find a price row. Status transitions: the sync
 * writes `pending` and `retired`; admins write `listed` and `delisted`.
 * The `approved_by` column is `approvedByUserId` in TS because the bare
 * `approvedBy` name is reserved for inbox reply approvals (guarded by
 * tests/unit/inbox/reply-approval-callers.test.ts).
 */
export const llmModels = pgTable('llm_models', {
  modelId: text('model_id').primaryKey(),
  provider: text('provider').references(() => llmProviders.key).notNull(),
  source: text('source').$type<RegistrySource>().notNull(),
  status: text('status').$type<RegistryStatus>().default('pending').notNull(),
  label: text('label'),
  tier: text('tier').$type<RegistryTier>(),
  transport: text('transport').$type<ModelTransport>().default('completions').notNull(),
  capabilities: jsonb('capabilities').$type<RegistryCapabilities>(),
  /** The retail basis, USD per 1K tokens as decimal strings. Retail is computed at read time and never stored. */
  listPrice: jsonb('list_price').$type<ModelPricing>(),
  listPriceSource: text('list_price_source').$type<ListPriceSource>(),
  listPriceCheckedAt: timestamp('list_price_checked_at'),
  /** How the user is billed for cached tokens on every route: the home provider's discount, or the input rate (plan section 7). */
  cacheBilling: text('cache_billing').$type<CacheBilling>().default('discount').notNull(),
  lifecycle: jsonb('lifecycle').$type<RegistryLifecycle>(),
  discovery: jsonb('discovery').$type<RegistryDiscovery>(),
  /** Consecutive syncs the model was absent from discovery; at 3 a listed or delisted discovered row retires. */
  missingSyncs: integer('missing_syncs').default(0).notNull(),
  firstSeenAt: timestamp('first_seen_at').defaultNow().notNull(),
  lastSeenAt: timestamp('last_seen_at').defaultNow().notNull(),
  approvedAt: timestamp('approved_at'),
  approvedByUserId: uuid('approved_by').references(() => users.id, { onDelete: 'set null' }),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  updatedBy: uuid('updated_by').references(() => users.id, { onDelete: 'set null' }),
}, (table) => ({
  providerStatusIdx: index('llm_models_provider_status_idx').on(table.provider, table.status),
  sourceCheck: check('llm_models_source_check', sql`${table.source} IN ('catalog', 'bedrock', 'admin')`),
  statusCheck: check('llm_models_status_check', sql`${table.status} IN ('pending', 'listed', 'delisted', 'retired')`),
  tierCheck: check('llm_models_tier_check', sql`${table.tier} IS NULL OR ${table.tier} IN ('fastest', 'balanced', 'smartest')`),
  transportCheck: check('llm_models_transport_check', sql`${table.transport} IN ('completions', 'realtime', 'local')`),
  cacheBillingCheck: check('llm_models_cache_billing_check', sql`${table.cacheBilling} IN ('discount', 'input')`),
}));

/**
 * One model on one provider, in that model's ordered chain (`priority`, 1
 * first; the admin write renumbers 1..n, so no unique constraint on it).
 * `wire` is what the provider receives; `cost` is the provider's price in
 * USD per 1K, confirmed only with a `cost_checked_at`; a route with no
 * confirmed cost, or a cost at or above the model's retail on any leg, never
 * serves (lib/models/registry-margin.ts). Routes are never deleted:
 * `enabled = false` is the off switch, and a settled charge may still point
 * at the row.
 */
export const llmModelRoutes = pgTable('llm_model_routes', {
  id: uuid('id').defaultRandom().primaryKey(),
  modelId: text('model_id').references(() => llmModels.modelId).notNull(),
  provider: text('provider').references(() => llmProviders.key).notNull(),
  priority: integer('priority').notNull(),
  enabled: boolean('enabled').default(true).notNull(),
  wire: jsonb('wire').$type<RouteWire>().notNull(),
  cost: jsonb('cost').$type<ModelPricing>(),
  costSource: text('cost_source').$type<RouteCostSource>(),
  costVersion: text('cost_version'),
  costCheckedAt: timestamp('cost_checked_at'),
  costNote: text('cost_note'),
  /** null unknown (counts as "may report"), true, false (the probe sets it; the settle flips it back to null on a surprise). */
  reportsCachedTokens: boolean('reports_cached_tokens'),
  probe: jsonb('probe').$type<RegistryProbe>(),
  source: text('source').$type<RegistrySource>().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  updatedBy: uuid('updated_by').references(() => users.id, { onDelete: 'set null' }),
}, (table) => ({
  modelProviderUq: unique('llm_model_routes_model_provider_key').on(table.modelId, table.provider),
  modelPriorityIdx: index('llm_model_routes_model_priority_idx').on(table.modelId, table.priority),
  providerIdx: index('llm_model_routes_provider_idx').on(table.provider),
  sourceCheck: check('llm_model_routes_source_check', sql`${table.source} IN ('catalog', 'bedrock', 'admin')`),
  priorityCheck: check('llm_model_routes_priority_check', sql`${table.priority} >= 1`),
}));

/** Append-only: every sync and admin write leaves one of these, naming the model, the provider or the route it touched (at least one, checked by the writer). */
export const llmModelAudit = pgTable('llm_model_audit', {
  id: uuid('id').defaultRandom().primaryKey(),
  modelId: text('model_id'),
  provider: text('provider'),
  routeId: uuid('route_id'),
  action: text('action').$type<RegistryAuditAction>().notNull(),
  oldValue: jsonb('old_value'),
  newValue: jsonb('new_value'),
  changedBy: uuid('changed_by').references(() => users.id, { onDelete: 'set null' }),
  note: text('note'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  modelIdx: index('llm_model_audit_model_idx').on(table.modelId, table.createdAt),
}));

/** One row per sync run (scheduled, admin "Sync now", or the CLI), trimmed after 90 days by the sync itself. */
export const llmModelSyncRuns = pgTable('llm_model_sync_runs', {
  id: uuid('id').defaultRandom().primaryKey(),
  trigger: text('trigger').$type<RegistrySyncTrigger>().notNull(),
  startedAt: timestamp('started_at').defaultNow().notNull(),
  finishedAt: timestamp('finished_at'),
  outcome: text('outcome').$type<RegistrySyncOutcome>(),
  counts: jsonb('counts').$type<RegistrySyncCounts>(),
  priceVersions: jsonb('price_versions').$type<RegistryPriceVersions>(),
  error: text('error'),
  ranBy: uuid('ran_by').references(() => users.id, { onDelete: 'set null' }),
  host: text('host'),
}, (table) => ({
  startedIdx: index('llm_model_sync_runs_started_idx').on(table.startedAt),
  triggerCheck: check('llm_model_sync_runs_trigger_check', sql`${table.trigger} IN ('schedule', 'admin', 'cli')`),
  outcomeCheck: check('llm_model_sync_runs_outcome_check', sql`${table.outcome} IS NULL OR ${table.outcome} IN ('ok', 'partial', 'failed')`),
}));

// H5: manual withdrawal queue (ToS §21.7). Amounts in nanocredits. One open request per
// user (partial unique below). Mark-paid is transactional with the ledger debit and
// idempotent via the status transition — see lib/payments/earnings.ts.
export const withdrawalRequests = pgTable('withdrawal_requests', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  amount: bigint('amount', { mode: 'bigint' }).notNull(),
  status: withdrawalRequestStatusEnum('status').default('requested').notNull(),
  // Payout coordinates the creator supplied. Stored RAW at rest; masked by the
  // serializers in lib/payments/withdrawals.ts (creators and the admin queue see
  // only a hint like "Account ending 6789"), and readable in full solely through
  // the single-request admin payout read. Never widen that surface.
  // Tax/screening states advance as the admin works the queue (H8 conditions);
  method: text('method'),
  details: jsonb('details'),
  taxFormStatus: text('tax_form_status').default('pending').notNull(),
  screeningStatus: text('screening_status').default('pending').notNull(),
  ledgerTxnId: uuid('ledger_txn_id'),
  reviewNote: text('review_note'),
  requestedAt: timestamp('requested_at').defaultNow().notNull(),
  reviewedAt: timestamp('reviewed_at'),
  reviewedBy: uuid('reviewed_by').references(() => users.id),
  paidAt: timestamp('paid_at'),
  paidBy: uuid('paid_by').references(() => users.id),
}, (table) => ({
  userIdx: index('withdrawal_requests_user_idx').on(table.userId, table.requestedAt),
  statusIdx: index('withdrawal_requests_status_idx').on(table.status, table.requestedAt),
  oneOpenPerUserIdx: uniqueIndex('withdrawal_requests_one_open_idx')
    .on(table.userId)
    .where(sql`status IN ('requested', 'in_review')`),
}));

// H4: Creator Fund grants (ToS §21.12 as amended by G7). The grant record carries the
// program-terms acceptance and publicity consent; crediting writes a 'creator_grant'
// ledger row against earnings_balance and links it here. Endpoints stay dark behind
// settings key 'grants.enabled' until counsel signs off Q12 and G1 deploys.
export const creatorGrants = pgTable('creator_grants', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  amount: bigint('amount', { mode: 'bigint' }).notNull(),
  reason: text('reason'),
  fundTermsAcceptedAt: timestamp('fund_terms_accepted_at'),
  publicityConsent: boolean('publicity_consent').default(false).notNull(),
  status: text('status').default('recorded').notNull(), // recorded | credited | reclaimed
  ledgerTxnId: uuid('ledger_txn_id'),
  createdBy: uuid('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('creator_grants_user_idx').on(table.userId, table.createdAt),
}));

// Payment tokens (JWT jti stored; balances in nanocredits)
export const paymentTokens = pgTable('payment_tokens', {
  id: uuid('id').defaultRandom().primaryKey(), // jti claim
  userId: uuid('user_id').references(() => users.id).notNull(),
  accessTokenId: uuid('access_token_id').references(() => accessTokens.id),
  initialBalance: bigint('initial_balance', { mode: 'bigint' }).notNull(),
  availableBalance: bigint('available_balance', { mode: 'bigint' }).notNull(),
  reservedBalance: bigint('reserved_balance', { mode: 'bigint' }).notNull().default(BigInt(0)), // Amount reserved by locks + delegations
  audience: jsonb('audience'), // array of allowed recipient ids (aud claim)
  status: paymentTokenStatusEnum('status').default('active').notNull(),
  balanceSource: text('balance_source').default('real'), // 'real' | 'plan' | 'demo' | 'earnings' (A1)
  parentTokenId: uuid('parent_token_id'), // self-ref FK for delegation chains (null for root tokens)
  chain: jsonb('chain').$type<string[]>().default([]), // delegation path: array of agent IDs
  commissions: jsonb('commissions').$type<{ rate: number; beneficiary: string; label: string }[]>(),
  expiresAt: timestamp('expires_at').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('payment_tokens_user_idx').on(table.userId),
  accessTokenIdx: index('payment_tokens_access_token_idx').on(table.accessTokenId),
  statusExpiresIdx: index('payment_tokens_status_expires_idx').on(table.status, table.expiresAt),
  parentTokenIdx: index('payment_tokens_parent_idx').on(table.parentTokenId),
}));

// Payment locks - job-specific budget carved from a token
export const paymentLocks = pgTable('payment_locks', {
  id: uuid('id').defaultRandom().primaryKey(),
  tokenId: uuid('token_id').references(() => paymentTokens.id).notNull(),
  agentId: uuid('agent_id').references(() => users.id).notNull(),
  amount: bigint('amount', { mode: 'bigint' }).notNull(), // max budget for this job
  settledAmount: bigint('settled_amount', { mode: 'bigint' }).notNull().default(BigInt(0)),
  status: paymentLockStatusEnum('status').default('active').notNull(),
  // Migration 0063: the W6 sweep's handle. Lazy cleanup was unreachable once a
  // token expired, so orphaned active locks grew without bound.
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  tokenIdx: index('payment_locks_token_idx').on(table.tokenId),
  agentIdx: index('payment_locks_agent_idx').on(table.agentId),
  statusIdx: index('payment_locks_status_idx').on(table.status),
}));

// Charges against payment tokens
export const paymentCharges = pgTable('payment_charges', {
  id: uuid('id').defaultRandom().primaryKey(),
  tokenId: uuid('token_id').references(() => paymentTokens.id).notNull(),
  recipientId: uuid('recipient_id').references(() => users.id).notNull(),
  recipientAccessTokenId: uuid('recipient_access_token_id').references(() => accessTokens.id),
  amount: bigint('amount', { mode: 'bigint' }).notNull(),
  description: text('description'),
  resource: text('resource'),
  cid: text('cid'), // IPFS content hash for receipt
  // Migration 0063: settlement already knows both and used to drop them.
  // chargeType: platform_llm | byok_llm | agent_fee | mediagen | a2a | gate |
  // voice | other. costNanocents: provider COGS at settle where known (LLM
  // list cost), making realized inference margin queryable.
  chargeType: text('charge_type'),
  costNanocents: bigint('cost_nanocents', { mode: 'bigint' }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  tokenIdx: index('payment_charges_token_idx').on(table.tokenId),
  recipientIdx: index('payment_charges_recipient_idx').on(table.recipientId),
}));

// Idempotent settles (migration 0091, 2026-09-26). One row per
// (recipient, Idempotency-Key), inserted FIRST inside the settle transaction
// so a repeated or concurrent settle with the same key charges nothing and is
// answered the stored `result` (charged, remaining, work, partial, unbilled;
// nanocredit strings). See lib/payments/settle-idempotency.ts.
//
// The ON DELETE rules mirror the migration file, which says what each does
// to the audit trail: a guard goes with the recipient, lock or token it
// guards (cascade), and outlives its charge row (set null).
export const paymentSettleKeys = pgTable('payment_settle_keys', {
  id: uuid('id').defaultRandom().primaryKey(),
  recipientId: uuid('recipient_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  idempotencyKey: text('idempotency_key').notNull(),
  lockId: uuid('lock_id').references(() => paymentLocks.id, { onDelete: 'cascade' }).notNull(),
  tokenId: uuid('token_id').references(() => paymentTokens.id, { onDelete: 'cascade' }).notNull(),
  chargeId: uuid('charge_id').references(() => paymentCharges.id, { onDelete: 'set null' }),
  // SHA-256 over the request fields that decide the charge (wave-0 review
  // finding 5): a key reused for a different request is refused, never
  // replayed. Null on a row an older pod wrote.
  requestFingerprint: text('request_fingerprint'),
  // Nanocredit strings, never numbers: JSON has no bigint.
  result: jsonb('result').$type<{ charged: string; remaining: string; work?: string; partial?: boolean; unbilled?: string }>().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  recipientKeyUq: unique('payment_settle_keys_recipient_key_uq').on(table.recipientId, table.idempotencyKey),
  createdIdx: index('payment_settle_keys_created_idx').on(table.createdAt),
}));

// Platform-wide single use for the x402 credits nonces of priced custom HTTP
// endpoints (S-297) is NOT a table: it is a Redis `SET NX EX` key per nonce
// (lib/payments/x402-nonce-claims.ts), the house pattern for MPP challenge
// ids and Web Bot Auth nonces. Migration 0094 that held it was dropped
// before any environment applied it (2026-09-27).

// Spending overrides per user/agent pair
export const spendingOverrides = pgTable('spending_overrides', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  targetAgentId: uuid('target_agent_id').references(() => users.id), // null = default for all agents
  maxPerRequest: bigint('max_per_request', { mode: 'bigint' }),      // nanocredits, null = no limit
  maxDaily: bigint('max_daily', { mode: 'bigint' }),                  // nanocredits, null = no limit
  autoApproveThreshold: bigint('auto_approve_threshold', { mode: 'bigint' }), // below = auto-topup, above = confirm
  overrideType: overrideTypeEnum('override_type').default('permanent').notNull(), // permanent | one_time | block
  usesRemaining: integer('uses_remaining'),  // for one_time: starts at 1, decrements to 0; null for permanent/block
  expiresAt: timestamp('expires_at'),        // null = no expiry
  source: overrideSourceEnum('source').default('manual').notNull(), // how override was created
  requestContext: jsonb('request_context'),   // NLI call context that triggered approval (agent, message hash, amount)
  // Per-app compute-share override (fn-quota plan D4, 2026-08-10): percent
  // of the user's daily compute account bucket this app may use, replacing
  // the matrix default (perAppShare, 40). User-set via the wallet Overrides
  // tab; capped at 100 in the gate. Null = matrix default.
  computeSharePct: integer('compute_share_pct'),
  isEnabled: boolean('is_enabled').default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('spending_overrides_user_idx').on(table.userId),
  pairIdx: index('spending_overrides_pair_idx').on(table.userId, table.targetAgentId),
}));

// Notifications
export const notifications = pgTable('notifications', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  type: text('type').notNull(), // payment_required, message_summary, mention, reply, follow, system
  title: text('title').notNull(),
  body: text('body'),
  data: jsonb('data').default({}), // type-specific payload (routing info, amounts, etc.)
  isRead: boolean('is_read').default(false).notNull(),
  actionTaken: text('action_taken'), // approved, denied, auto_approved, expired
  sourceType: text('source_type'), // agent, user, channel, post, system
  sourceId: text('source_id'),
  // 'normal' (default) | 'critical' — critical forces always-on push +
  // notification.critical WS event so callers can route to in-chat banners,
  // modal toasts, and persistent system notifications. See docs/internal/
  // guides/notifications.md and lib/notifications/service.ts.
  urgency: text('urgency').default('normal').notNull(),
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  unreadIdx: index('notifications_unread_idx').on(table.userId, table.isRead, table.updatedAt),
  listIdx: index('notifications_list_idx').on(table.userId, table.updatedAt),
  typeIdx: index('notifications_type_idx').on(table.userId, table.type),
  urgencyIdx: index('notifications_urgency_idx').on(table.userId, table.urgency, table.isRead),
}));

// Notification mutes
export const notificationMutes = pgTable('notification_mutes', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  sourceType: text('source_type').notNull(), // agent, user, channel, post
  sourceId: text('source_id').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userSourceIdx: uniqueIndex('notification_mutes_user_source_idx').on(table.userId, table.sourceType, table.sourceId),
}));

// Intents — canonical store for API-registered agent intents.
// Milvus holds the vector index; this table is the durable source of truth.
export const intents = pgTable('intents', {
  id: uuid('id').defaultRandom().primaryKey(),
  intent: text('intent').notNull(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  agentId: uuid('agent_id').references(() => users.id).notNull(),
  description: text('description').default('').notNull(),
  rank: integer('rank').default(0).notNull(),
  url: text('url').default('').notNull(),
  protocol: text('protocol').default('completions').notNull(),
  subpath: text('subpath').default('').notNull(),
  expiresAt: timestamp('expires_at'),
  latitude: doublePrecision('latitude').default(0).notNull(),
  longitude: doublePrecision('longitude').default(0).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  /* WHICH NAMESPACE THIS INTENT BELONGS TO, on the SOURCE OF TRUTH (migration
     0082, S-126). The empty string is platform-wide, the same value and the
     same meaning as the Milvus column, so neither store has to translate.

     It lived only in Milvus until 2026-09-17, and Milvus is a derived index
     that `rebuildIntents` reconstructs from these columns. A rebuild therefore
     found no scope, stamped every row platform-wide and said it had succeeded,
     which moved two sentences a person wrote for one conference into the
     global index. A consent boundary cannot live only in something that gets
     rebuilt from somewhere else. */
  namespace: text('namespace').default('').notNull(),
}, (table) => ({
  agentIdx: index('intents_agent_id_idx').on(table.agentId),
  userIdx: index('intents_user_id_idx').on(table.userId),
  expiresIdx: index('intents_expires_at_idx').on(table.expiresAt),
  namespaceAgentIdx: index('intents_namespace_agent_idx').on(table.namespace, table.agentId),
}));

// Intent Subscriptions (patent Claim 10, FIG. 4)
export const intentSubscriptions = pgTable('intent_subscriptions', {
  id: uuid('id').defaultRandom().primaryKey(),
  subscriberId: uuid('subscriber_id').references(() => users.id).notNull(),
  queryText: text('query_text').notNull(),
  threshold: decimal('threshold', { precision: 4, scale: 3 }).default('0.7').notNull(),
  callbackUrl: text('callback_url'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  expiresAt: timestamp('expires_at'),
}, (table) => ({
  subscriberIdx: index('intent_subscriptions_subscriber_idx').on(table.subscriberId),
  expiresIdx: index('intent_subscriptions_expires_idx').on(table.expiresAt),
}));

// Relations
export const usersRelations = relations(users, ({ many, one }) => ({
  posts: many(posts),
  comments: many(comments),
  votes: many(votes),
  followers: many(follows, { relationName: 'followers' }),
  following: many(follows, { relationName: 'following' }),
  ownedAgents: many(users, { relationName: 'ownedAgents' }),
  owner: one(users, {
    fields: [users.ownerId],
    references: [users.id],
    relationName: 'ownedAgents',
  }),
  userTags: many(userTags),
  channelMemberships: many(channelMembers),
  channelPermissions: many(channelPermissions),
  ownedChannels: many(channels, { relationName: 'ownedChannels' }),
  domainVerifications: many(domainVerifications),
  grantedPermissions: many(channelPermissions, { relationName: 'grantedPermissions' }),
  accessTokens: many(accessTokens),
  pinnedPosts: many(pinnedPosts),
  savedPosts: many(savedPosts),
  activeConfig: one(agentConfigs, {
    fields: [users.activeConfigId],
    references: [agentConfigs.id],
  }),
  apiKey: one(accessTokens, {
    fields: [users.apiKeyId],
    references: [accessTokens.id],
  }),
}));

export const agentConfigsRelations = relations(agentConfigs, ({ one }) => ({
  agent: one(users, {
    fields: [agentConfigs.agentId],
    references: [users.id],
  }),
  createdByUser: one(users, {
    fields: [agentConfigs.createdBy],
    references: [users.id],
  }),
}));

export const agentTemplatesRelations = relations(agentTemplates, ({ many }) => ({
  batches: many(agentBatches),
}));

export const agentBatchesRelations = relations(agentBatches, ({ one }) => ({
  template: one(agentTemplates, {
    fields: [agentBatches.templateId],
    references: [agentTemplates.id],
  }),
}));

export const accessTokensRelations = relations(accessTokens, ({ one }) => ({
  user: one(users, {
    fields: [accessTokens.userId],
    references: [users.id],
  }),
  agent: one(users, {
    fields: [accessTokens.agentId],
    references: [users.id],
  }),
}));

export const balanceTransactionsRelations = relations(balanceTransactions, ({ one }) => ({
  user: one(users, {
    fields: [balanceTransactions.userId],
    references: [users.id],
  }),
}));

export const paymentTokensRelations = relations(paymentTokens, ({ one, many }) => ({
  user: one(users, {
    fields: [paymentTokens.userId],
    references: [users.id],
  }),
  accessToken: one(accessTokens, {
    fields: [paymentTokens.accessTokenId],
    references: [accessTokens.id],
  }),
  parentToken: one(paymentTokens, {
    fields: [paymentTokens.parentTokenId],
    references: [paymentTokens.id],
    relationName: 'delegatedTokens',
  }),
  delegatedTokens: many(paymentTokens, { relationName: 'delegatedTokens' }),
  locks: many(paymentLocks),
}));

export const paymentLocksRelations = relations(paymentLocks, ({ one }) => ({
  token: one(paymentTokens, {
    fields: [paymentLocks.tokenId],
    references: [paymentTokens.id],
  }),
  agent: one(users, {
    fields: [paymentLocks.agentId],
    references: [users.id],
  }),
}));

export const paymentChargesRelations = relations(paymentCharges, ({ one }) => ({
  token: one(paymentTokens, {
    fields: [paymentCharges.tokenId],
    references: [paymentTokens.id],
  }),
  recipient: one(users, {
    fields: [paymentCharges.recipientId],
    references: [users.id],
  }),
}));

export const spendingOverridesRelations = relations(spendingOverrides, ({ one }) => ({
  user: one(users, {
    fields: [spendingOverrides.userId],
    references: [users.id],
  }),
  targetAgent: one(users, {
    fields: [spendingOverrides.targetAgentId],
    references: [users.id],
  }),
}));

export const featuredContentRelations = relations(featuredContent, ({ one }) => ({
  post: one(posts, {
    fields: [featuredContent.postId],
    references: [posts.id],
  }),
  targetUser: one(users, {
    fields: [featuredContent.targetUserId],
    references: [users.id],
    relationName: 'featuredTargetUser',
  }),
  targetChannel: one(channels, {
    fields: [featuredContent.targetChannelId],
    references: [channels.id],
  }),
  creator: one(users, {
    fields: [featuredContent.createdBy],
    references: [users.id],
    relationName: 'featuredCreator',
  }),
}));

export const channelCategoriesRelations = relations(channelCategories, ({ many }) => ({
  channels: many(channels),
}));

export const channelsRelations = relations(channels, ({ one, many }) => ({
  category: one(channelCategories, {
    fields: [channels.categoryId],
    references: [channelCategories.id],
  }),
  parent: one(channels, {
    fields: [channels.parentId],
    references: [channels.id],
    relationName: 'subchannels',
  }),
  subchannels: many(channels, { relationName: 'subchannels' }),
  posts: many(posts),
  members: many(channelMembers),
  permissions: many(channelPermissions),
  channelTags: many(channelTags),
  createdBy: one(users, {
    fields: [channels.createdById],
    references: [users.id],
  }),
  owner: one(users, {
    fields: [channels.ownerId],
    references: [users.id],
    relationName: 'ownedChannels',
  }),
  domainVerifications: many(domainVerifications),
  aliases: many(channelAliases),
}));

export const tagsRelations = relations(tags, ({ many }) => ({
  postTags: many(postTags),
  userTags: many(userTags),
  channelTags: many(channelTags),
}));

export const postsRelations = relations(posts, ({ one, many }) => ({
  author: one(users, {
    fields: [posts.authorId],
    references: [users.id],
  }),
  channel: one(channels, {
    fields: [posts.channelId],
    references: [channels.id],
  }),
  quotedPost: one(posts, {
    fields: [posts.quotedPostId],
    references: [posts.id],
    relationName: 'quotedPosts',
  }),
  quotedBy: many(posts, { relationName: 'quotedPosts' }),
  comments: many(comments),
  votes: many(votes),
  postTags: many(postTags),
  subscriptions: many(postSubscriptions),
  pinnedBy: many(pinnedPosts),
  savedBy: many(savedPosts),
  edits: many(postEdits),
}));

export const postEditsRelations = relations(postEdits, ({ one }) => ({
  post: one(posts, {
    fields: [postEdits.postId],
    references: [posts.id],
  }),
  editor: one(users, {
    fields: [postEdits.editorId],
    references: [users.id],
  }),
}));

export const postSubscriptionsRelations = relations(postSubscriptions, ({ one }) => ({
  user: one(users, {
    fields: [postSubscriptions.userId],
    references: [users.id],
  }),
  post: one(posts, {
    fields: [postSubscriptions.postId],
    references: [posts.id],
  }),
}));

export const pinnedPostsRelations = relations(pinnedPosts, ({ one }) => ({
  user: one(users, {
    fields: [pinnedPosts.userId],
    references: [users.id],
  }),
  post: one(posts, {
    fields: [pinnedPosts.postId],
    references: [posts.id],
  }),
}));

export const savedPostsRelations = relations(savedPosts, ({ one }) => ({
  user: one(users, {
    fields: [savedPosts.userId],
    references: [users.id],
  }),
  post: one(posts, {
    fields: [savedPosts.postId],
    references: [posts.id],
  }),
}));

export const postTagsRelations = relations(postTags, ({ one }) => ({
  post: one(posts, {
    fields: [postTags.postId],
    references: [posts.id],
  }),
  tag: one(tags, {
    fields: [postTags.tagId],
    references: [tags.id],
  }),
  addedBy: one(users, {
    fields: [postTags.addedById],
    references: [users.id],
  }),
}));

export const channelTagsRelations = relations(channelTags, ({ one }) => ({
  channel: one(channels, {
    fields: [channelTags.channelId],
    references: [channels.id],
  }),
  tag: one(tags, {
    fields: [channelTags.tagId],
    references: [tags.id],
  }),
}));

export const commentsRelations = relations(comments, ({ one, many }) => ({
  post: one(posts, {
    fields: [comments.postId],
    references: [posts.id],
  }),
  author: one(users, {
    fields: [comments.authorId],
    references: [users.id],
  }),
  parent: one(comments, {
    fields: [comments.parentId],
    references: [comments.id],
    relationName: 'replies',
  }),
  replies: many(comments, { relationName: 'replies' }),
  votes: many(votes),
}));

export const votesRelations = relations(votes, ({ one }) => ({
  user: one(users, {
    fields: [votes.userId],
    references: [users.id],
  }),
  post: one(posts, {
    fields: [votes.postId],
    references: [posts.id],
  }),
  comment: one(comments, {
    fields: [votes.commentId],
    references: [comments.id],
  }),
}));

export const followsRelations = relations(follows, ({ one }) => ({
  follower: one(users, {
    fields: [follows.followerId],
    references: [users.id],
    relationName: 'following',
  }),
  following: one(users, {
    fields: [follows.followingId],
    references: [users.id],
    relationName: 'followers',
  }),
}));

export const channelMembersRelations = relations(channelMembers, ({ one }) => ({
  channel: one(channels, {
    fields: [channelMembers.channelId],
    references: [channels.id],
  }),
  user: one(users, {
    fields: [channelMembers.userId],
    references: [users.id],
  }),
}));

export const channelPermissionsRelations = relations(channelPermissions, ({ one }) => ({
  channel: one(channels, {
    fields: [channelPermissions.channelId],
    references: [channels.id],
  }),
  user: one(users, {
    fields: [channelPermissions.userId],
    references: [users.id],
  }),
  grantedByUser: one(users, {
    fields: [channelPermissions.grantedBy],
    references: [users.id],
    relationName: 'grantedPermissions',
  }),
}));

export const reservedPrefixesRelations = relations(reservedPrefixes, () => ({}));

export const domainVerificationsRelations = relations(domainVerifications, ({ one, many }) => ({
  user: one(users, {
    fields: [domainVerifications.userId],
    references: [users.id],
  }),
  channel: one(channels, {
    fields: [domainVerifications.channelId],
    references: [channels.id],
  }),
  appBindings: many(domainAppBindings),
}));

export const domainAppBindingsRelations = relations(domainAppBindings, ({ one }) => ({
  verification: one(domainVerifications, {
    fields: [domainAppBindings.verificationId],
    references: [domainVerifications.id],
  }),
  content: one(content, {
    fields: [domainAppBindings.contentId],
    references: [content.id],
  }),
}));

export const tldsRelations = relations(tlds, () => ({}));

export const channelAliasesRelations = relations(channelAliases, ({ one }) => ({
  channel: one(channels, {
    fields: [channelAliases.channelId],
    references: [channels.id],
  }),
}));

export const userTagsRelations = relations(userTags, ({ one }) => ({
  user: one(users, {
    fields: [userTags.userId],
    references: [users.id],
  }),
  tag: one(tags, {
    fields: [userTags.tagId],
    references: [tags.id],
  }),
}));

export const referralCodesRelations = relations(referralCodes, ({ one, many }) => ({
  owner: one(users, {
    fields: [referralCodes.userId],
    references: [users.id],
  }),
  referrals: many(referrals),
}));

export const referralsRelations = relations(referrals, ({ one }) => ({
  referrer: one(users, {
    fields: [referrals.referrerId],
    references: [users.id],
  }),
  referred: one(users, {
    fields: [referrals.referredId],
    references: [users.id],
  }),
  referralCode: one(referralCodes, {
    fields: [referrals.referralCodeId],
    references: [referralCodes.id],
  }),
}));

// ==========================================
// MESSAGING SYSTEM (H2H, H2A, A2H, A2A)
// ==========================================

// Chats - conversations between users/agents
export const chats = pgTable('chats', {
  id: uuid('id').defaultRandom().primaryKey(),
  type: chatTypeEnum('type').default('dm').notNull(),
  name: text('name'), // NULL for DMs, custom for groups
  avatarUrl: text('avatar_url'), // For group chats
  createdById: uuid('created_by_id').references(() => users.id),
  parentChatId: uuid('parent_chat_id').references((): any => chats.id, { onDelete: 'cascade' }),
  delegatedAgentId: uuid('delegated_agent_id').references(() => users.id, { onDelete: 'set null' }),
  lastMessageAt: timestamp('last_message_at'),
  lastMessagePreview: text('last_message_preview'),
  metadata: jsonb('metadata').default({}),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  typeIdx: index('chats_type_idx').on(table.type),
  lastMessageIdx: index('chats_last_message_idx').on(table.lastMessageAt),
  delegateAutoIdx: uniqueIndex('chats_delegate_auto_idx')
    .on(table.parentChatId, table.delegatedAgentId)
    .where(sql`type = 'delegate'`),
  delegateEphemeralIdx: uniqueIndex('chats_delegate_ephemeral_idx')
    .on(table.createdById, table.delegatedAgentId)
    .where(sql`type = 'delegate-ephemeral' AND parent_chat_id IS NULL`),
}));

// Chat participants
export const chatParticipants = pgTable('chat_participants', {
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'cascade' }).notNull(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  role: text('role').default('member'), // 'owner', 'admin', 'member'
  lastReadAt: timestamp('last_read_at'),
  unreadCount: integer('unread_count').default(0),
  isMuted: boolean('is_muted').default(false),
  isStarred: boolean('is_starred').default(false),
  isDeleted: boolean('is_deleted').default(false),
  joinedAt: timestamp('joined_at').defaultNow().notNull(),
}, (table) => ({
  pk: uniqueIndex('chat_participants_pk').on(table.chatId, table.userId),
  userChatsIdx: index('chat_participants_user_idx').on(table.userId),
}));

// Messages - UAMP compatible multimodal messages
export const messages = pgTable('messages', {
  id: uuid('id').defaultRandom().primaryKey(),
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'cascade' }).notNull(),
  senderId: uuid('sender_id').references(() => users.id, { onDelete: 'set null' }),
  
  // UAMP message format
  role: text('role').default('user').notNull(), // 'user', 'assistant', 'system', 'tool'
  content: text('content'), // Simple text content (optional with content_items)
  contentItems: jsonb('content_items'), // UAMP ContentItem[] for multimodal
  
  // Message classification (source only: human or agent)
  senderKind: accountTypeEnum('sender_kind').notNull(),

  // Legacy media fields (for backward compatibility, prefer content_items)
  mediaUrl: text('media_url'),
  mediaType: text('media_type'), // 'image', 'video', 'file', 'audio'
  linkUrl: text('link_url'),
  linkMeta: jsonb('link_meta'), // OG metadata
  
  // Reply/thread support
  replyToId: uuid('reply_to_id'),
  
  // Tool call support (UAMP compatible)
  toolCallId: text('tool_call_id'), // For tool results
  toolName: text('tool_name'), // For tool calls
  toolArguments: text('tool_arguments'), // JSON string for tool calls
  
  // Status
  isEdited: boolean('is_edited').default(false),
  isDeleted: boolean('is_deleted').default(false),
  
  // UAMP event metadata
  uampEventId: text('uamp_event_id'), // Original UAMP event_id if from WebAgent
  metadata: jsonb('metadata').default({}),
  
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  chatIdx: index('messages_chat_idx').on(table.chatId),
  senderIdx: index('messages_sender_idx').on(table.senderId),
  createdAtIdx: index('messages_created_at_idx').on(table.createdAt),
  chatCreatedIdx: index('messages_chat_created_idx').on(table.chatId, table.createdAt),
  toolCallIdx: index('messages_tool_call_idx').on(table.toolCallId),
  uampEventIdUniqueIdx: uniqueIndex('messages_uamp_event_id_idx').on(table.uampEventId).where(isNotNull(table.uampEventId)),
}));

// Content - user/agent and chat media (images, video, audio, documents, 3D)
// Stored flat at data/content/{uuid}.ext - ownership managed via content_links
export const content = pgTable('content', {
  id: uuid('id').defaultRandom().primaryKey(),
  creatorId: uuid('creator_id').references(() => users.id, { onDelete: 'set null' }),
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'set null' }),
  sourcePostId: uuid('source_post_id').references(() => posts.id, { onDelete: 'set null' }),
  displayName: text('display_name').notNull(),
  originalName: text('original_name').notNull(),
  storagePath: text('storage_path').notNull(),
  mimeType: text('mime_type').notNull(),
  size: bigint('size', { mode: 'bigint' }).notNull(),
  contentType: contentTypeEnum('content_type').notNull(),
  // Inline text body for small text-bearing content rows (function source files,
  // small snippets). NULL for binary content, which lives at storagePath.
  rawText: text('raw_text'),
  parentId: uuid('parent_id').references((): any => content.id, { onDelete: 'set null' }),
  thumbnailPath: text('thumbnail_path'),
  c2paManifest: jsonb('c2pa_manifest'),
  metadata: jsonb('metadata').default({}),
  // Plan v3-05 (ADR-v3-15) — widget remix lineage. All three nullable;
  // ON DELETE SET NULL so deleting a source preserves the remixed
  // widget at the cost of attribution.
  remixOfContentId: uuid('remix_of_content_id').references((): any => content.id, { onDelete: 'set null' }),
  remixOfPostId: uuid('remix_of_post_id').references(() => posts.id, { onDelete: 'set null' }),
  remixOfUserId: uuid('remix_of_user_id').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  creatorIdx: index('content_creator_idx').on(table.creatorId),
  chatIdx: index('content_chat_idx').on(table.chatId),
  contentTypeIdx: index('content_content_type_idx').on(table.contentType),
  parentIdx: index('content_parent_id_idx').on(table.parentId),
  createdAtIdx: index('content_created_at_idx').on(table.createdAt),
  // Partial indexes (created CONCURRENTLY in migration 0026a) only on
  // remixed rows — cheap on the hot path because most content is original.
  remixOfContentIdx: index('content_remix_of_content_idx')
    .on(table.remixOfContentId)
    .where(sql`${table.remixOfContentId} IS NOT NULL`),
  remixOfPostIdx: index('content_remix_of_post_idx')
    .on(table.remixOfPostId)
    .where(sql`${table.remixOfPostId} IS NOT NULL`),
  // metadata containment/key lookups (widget interface declarations,
  // projectMime, bundle kind) — R6; built CONCURRENTLY by migration 0054.
  metadataGinIdx: index('content_metadata_gin_idx').using(
    'gin',
    table.metadata.op('jsonb_path_ops'),
  ),
  // Pointer → referencing rows (ADR-0031): the public blob tier's
  // authorization lookup, the private tier's membership walk, and the CoW
  // shared-pointer check all select on storage_path equality. Partial —
  // most rows carry '' (folders, widget rows, inline bundle files). Built
  // CONCURRENTLY by migration 0070.
  storagePathIdx: index('content_storage_path_idx')
    .on(table.storagePath)
    .where(sql`${table.storagePath} <> ''`),
}));

// Content links - access grants. Each row is exactly one grant scoped to either
// a single user (user_id set, chat_id NULL) or a single chat (user_id NULL,
// chat_id set, covering all participants of that chat). Enforced by CHECK.
export const contentLinks = pgTable('content_links', {
  id: uuid('id').defaultRandom().primaryKey(),
  contentId: uuid('content_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }),
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'cascade' }),
  role: contentLinkRoleEnum('role').default('participant').notNull(),
  visibility: contentVisibilityEnum('visibility').default('private').notNull(),
  shareToken: text('share_token'),
  // How the grant came to exist: null = explicit invite / system; 'link' =
  // ENROLLED by opening the owner's share link on private content (read-only
  // capability redemption); 'invite' = MATERIALISED by claiming an email
  // invitation (content_invites, ADR-0036). Lets the share dialog label these
  // people and the owner revoke them like any other grantee. For 'invite' the
  // claimant is recorded on the content_invites row rather than here, so
  // revokeUsers can flip the invitation by (content_id, user_id) whichever of
  // its callers removed the person, and this hot ACL table takes no new DDL.
  addedVia: text('added_via'),
  addedAt: timestamp('added_at').defaultNow().notNull(),
}, (table) => ({
  subjectCheck: check(
    'content_links_subject_check',
    sql`((${table.userId} IS NOT NULL)::int + (${table.chatId} IS NOT NULL)::int) = 1`,
  ),
  userUniq: uniqueIndex('content_links_user_uniq')
    .on(table.contentId, table.userId)
    .where(sql`${table.userId} IS NOT NULL`),
  chatUniq: uniqueIndex('content_links_chat_uniq')
    .on(table.contentId, table.chatId)
    .where(sql`${table.chatId} IS NOT NULL`),
  userIdx: index('content_links_user_idx').on(table.userId),
  chatIdx: index('content_links_chat_idx').on(table.chatId),
  shareTokenIdx: uniqueIndex('content_links_share_token_idx').on(table.shareToken),
  // canAccessContent ACL resolution: content_id + visibility + role — hot
  // path of every workspace/share load (R6; built CONCURRENTLY by 0054).
  contentVisibilityRoleIdx: index('content_links_content_visibility_role_idx').on(
    table.contentId,
    table.visibility,
    table.role,
  ),
}));

// TYPED share tokens — one short-code link per (content, sharer, link type).
// Deliberately a SEPARATE table from content_links: that table is the ACL
// (one grant row per subject; visibility reads, the share-dialog people list
// and grant dedup all assume single-row semantics), while a user may hold
// MANY links to the same content — the default app link (content_links.
// share_token, kept for back compat) plus app-defined typed ones ("player"
// opens a deck straight into the presentation player). `linkType` is an
// app-specific slug the /w mount hands to the widget verbatim; NULL is
// reserved to mirror the legacy default link if these are ever migrated in.
// Access semantics at resolve time come from the sharer's LIVE grant row
// (role + link-shareable visibility), so revoking a grant kills the
// holder's typed links with it.
export const contentLinkTokens = pgTable('content_link_tokens', {
  id: uuid('id').defaultRandom().primaryKey(),
  contentId: uuid('content_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  linkType: text('link_type'),
  shareToken: text('share_token').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  tokenUniq: uniqueIndex('content_link_tokens_token_idx').on(table.shareToken),
  audienceTypeUniq: uniqueIndex('content_link_tokens_audience_type_idx')
    .on(table.contentId, table.userId, sql`coalesce(${table.linkType}, '')`),
  contentIdx: index('content_link_tokens_content_idx').on(table.contentId),
}));

// Portal-minted PUBLIC LINKS for an app's public pages (migration 0069).
//
// `/go/<code>` -> the app's `fn` with `params`, dispatched with
// `consumerOverride = ownerId` so the TENANT pays for their own public
// traffic (fn-quota-enforcement-plan.md D4 otherwise bills the app author,
// which is wrong for a multi-tenant app whose every tenant publishes pages).
//
// `projectId` / `ownerId` are stamped SERVER-side at mint from the caller's
// session + the widget's mounted project — never claimed by app code, which
// is what makes the billing principal trustworthy.
//
// Separate from `contentLinkTokens` ON PURPOSE: that rail grants read access
// on resolve; this one grants nothing at all.
export const appPublicLinks = pgTable('app_public_links', {
  id: uuid('id').defaultRandom().primaryKey(),
  code: text('code').notNull(),
  appId: uuid('app_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  projectId: uuid('project_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  ownerId: uuid('owner_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  fn: text('fn').notNull(),
  params: text('params'),
  label: text('label'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  revokedAt: timestamp('revoked_at'),
}, (table) => ({
  codeUniq: uniqueIndex('app_public_links_code_idx').on(table.code),
  ownerIdx: index('app_public_links_owner_idx').on(table.ownerId),
  // One LIVE code per target, so re-publishing reuses the printed link.
  targetUniq: uniqueIndex('app_public_links_target_idx')
    .on(table.projectId, table.fn, sql`coalesce(${table.params}, '')`)
    .where(sql`${table.revokedAt} IS NULL`),
}));

// Per-save snapshots of mutable content rows. Powers the History panel
// in the /content/[id] editor (View + Restore). One row per Save; the
// `version` column is monotonic per `contentId`.
//
// Body lives inline as bytea so a restore can be a single SQL read +
// file write — no second storage hop. We hard-cap retained rows to
// CONTENT_VERSIONS_KEEP (default 50) per content id; older ones are
// trimmed on every insert.
export const contentVersions = pgTable('content_versions', {
  id: uuid('id').defaultRandom().primaryKey(),
  contentId: uuid('content_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  version: integer('version').notNull(),
  body: customType<{ data: Buffer; notNull: true; default: false }>({
    dataType() { return 'bytea'; },
  })('body').notNull(),
  byteSize: bigint('byte_size', { mode: 'bigint' }).notNull(),
  mimeType: text('mime_type'),
  editedBy: uuid('edited_by').references(() => users.id, { onDelete: 'set null' }),
  /** Acting client display name when an agent/MCP client drove the save
   *  (e.g. "Claude Code"); null for direct human edits. `editedBy` stays the
   *  session user — history composes "Claude Code (for <user>)". */
  editedVia: text('edited_via'),
  command: text('command'),
  /** Human checkpoint name (explicit named checkpoints); null for autosaves. */
  label: text('label'),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  uniqContentVersion: uniqueIndex('content_versions_content_version_uniq').on(table.contentId, table.version),
  contentIdx: index('content_versions_content_idx').on(table.contentId, table.version),
}));

// Immutable, commit-like widget versions. One row per Publish/Update of a
// working widget `W`. Each version freezes the ENTIRE widget folder subtree
// into a `treeFolderId` snapshot (cloneContentSubtree, every file pointing at
// its immutable CAS blob) and pins a renderable `widgetContentId` (an immutable
// widget row whose bundle points at that frozen tree). Rows are NEVER mutated or
// deleted — they form a per-widget commit DAG via `parentVersionId` that a
// future git adapter maps 1:1 to commits (tree=`treeFolderId`, hash=`treeHash`).
export const widgetVersions = pgTable('widget_versions', {
  id: uuid('id').defaultRandom().primaryKey(),
  // The working "branch": the live, mutable widget row this version published.
  widgetId: uuid('widget_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  // Previous published version of THIS widget (commit parent). NULL = first.
  parentVersionId: uuid('parent_version_id').references((): any => widgetVersions.id, { onDelete: 'set null' }),
  // Frozen folder snapshot (the commit's TREE).
  // CASCADE, not `set null`: the column is NOT NULL, and 0030's `set null`
  // made every delete of a frozen row fail the not-null check, so no
  // published app could be deleted (2026-09-01). A version without its tree
  // is meaningless; retire it with the tree. Migration 0074 rewrites the FK.
  treeFolderId: uuid('tree_folder_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  // Merkle root over the frozen subtree (content address of the tree). Dedups a
  // no-op publish; becomes the git-tree hash in a future adapter.
  treeHash: text('tree_hash').notNull(),
  // Renderable immutable artifact: a widget row whose bundle points at the tree.
  // The published post repoints metadata.widgetContentId at this row.
  // CASCADE for the same reason as tree_folder_id above.
  widgetContentId: uuid('widget_content_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  authorId: uuid('author_id').references(() => users.id, { onDelete: 'set null' }),
  message: text('message'),
  // Best-effort cross-widget fork link: the SOURCE's published version when W is
  // a remix (resolved via W.remixOfPostId → post.metadata.publishedVersionId).
  forkParentVersionId: uuid('fork_parent_version_id').references((): any => widgetVersions.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  widgetIdx: index('widget_versions_widget_idx').on(table.widgetId, table.createdAt),
  parentIdx: index('widget_versions_parent_idx').on(table.parentVersionId),
  treeHashIdx: index('widget_versions_tree_hash_idx').on(table.treeHash),
}));

// ── Per-use attribution (project: per-use attribution) ───────────────────────
// ZERO dedicated tables, ZERO provenance registry. Markers are self-describing
// `@author` strings read straight from the (open-source) code — fairness is social
// (owner⇄contributor cross-verification + public discussion), not cryptographic.
// Raw usage signals ride `user_interactions`. The offline cron sizes each app's
// pool by EYE-TIME and splits it (env-configurable: ancestor / contributors /
// owner), writing per-recipient `balance_transactions` (type 'attribution_payout',
// idempotency-keyed) crediting earnings_balance (the Creator Rewards bucket; see
// lib/payments/earnings.ts for the type roster). Backmix proposals are `posts`.

// Entity subscriptions (channel / user / tag) - for discovery subscribe API
export const subscriptions = pgTable('subscriptions', {
  id: uuid('id').defaultRandom().primaryKey(),
  subscriberId: uuid('subscriber_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  targetType: text('target_type').notNull(), // 'channel', 'user', 'tag'
  targetId: uuid('target_id').notNull(),
  deliveryMode: text('delivery_mode').default('realtime').notNull(), // 'realtime', 'scheduled'
  scheduleFrequency: text('schedule_frequency'), // 'minutely', 'hourly', 'daily', 'weekly'
  callbackUrl: text('callback_url'),
  notifyOnPosts: boolean('notify_on_posts').default(true),
  notifyOnComments: boolean('notify_on_comments').default(false),
  failureCount: integer('failure_count').default(0).notNull(),
  lastDeliveredAt: timestamp('last_delivered_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  subscriberIdx: index('subscriptions_subscriber_idx').on(table.subscriberId),
  targetIdx: index('subscriptions_target_idx').on(table.targetType, table.targetId),
  subscriberTargetIdx: uniqueIndex('subscriptions_subscriber_target_idx').on(table.subscriberId, table.targetType, table.targetId),
}));

// Push subscriptions for Web Push
export const pushSubscriptions = pgTable('push_subscriptions', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  endpoint: text('endpoint').notNull(),
  p256dh: text('p256dh').notNull(), // Public key
  auth: text('auth').notNull(), // Auth secret
  userAgent: text('user_agent'),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  lastUsedAt: timestamp('last_used_at'),
}, (table) => ({
  userIdx: index('push_subscriptions_user_idx').on(table.userId),
  endpointIdx: index('push_subscriptions_endpoint_idx').on(table.endpoint),
}));

// Chat relations
export const chatsRelations = relations(chats, ({ one, many }) => ({
  createdBy: one(users, {
    fields: [chats.createdById],
    references: [users.id],
    relationName: 'chatCreator',
  }),
  parentChat: one(chats, {
    fields: [chats.parentChatId],
    references: [chats.id],
    relationName: 'parentChild',
  }),
  childChats: many(chats, { relationName: 'parentChild' }),
  delegatedAgent: one(users, {
    fields: [chats.delegatedAgentId],
    references: [users.id],
    relationName: 'chatDelegatedAgent',
  }),
  participants: many(chatParticipants),
  messages: many(messages),
}));

export const chatParticipantsRelations = relations(chatParticipants, ({ one }) => ({
  chat: one(chats, {
    fields: [chatParticipants.chatId],
    references: [chats.id],
  }),
  user: one(users, {
    fields: [chatParticipants.userId],
    references: [users.id],
  }),
}));

export const messagesRelations = relations(messages, ({ one }) => ({
  chat: one(chats, {
    fields: [messages.chatId],
    references: [chats.id],
  }),
  sender: one(users, {
    fields: [messages.senderId],
    references: [users.id],
  }),
  replyTo: one(messages, {
    fields: [messages.replyToId],
    references: [messages.id],
  }),
}));

export const contentRelations = relations(content, ({ one, many }) => ({
  creator: one(users, {
    fields: [content.creatorId],
    references: [users.id],
  }),
  chat: one(chats, {
    fields: [content.chatId],
    references: [chats.id],
  }),
  sourcePost: one(posts, {
    fields: [content.sourcePostId],
    references: [posts.id],
  }),
  links: many(contentLinks),
}));

export const contentLinksRelations = relations(contentLinks, ({ one }) => ({
  content: one(content, {
    fields: [contentLinks.contentId],
    references: [content.id],
  }),
  user: one(users, {
    fields: [contentLinks.userId],
    references: [users.id],
  }),
  chat: one(chats, {
    fields: [contentLinks.chatId],
    references: [chats.id],
  }),
}));

export const pushSubscriptionsRelations = relations(pushSubscriptions, ({ one }) => ({
  user: one(users, {
    fields: [pushSubscriptions.userId],
    references: [users.id],
  }),
}));

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
}));

export const notificationMutesRelations = relations(notificationMutes, ({ one }) => ({
  user: one(users, {
    fields: [notificationMutes.userId],
    references: [users.id],
  }),
}));

export const contentFlagsRelations = relations(contentFlags, ({ one }) => ({
  reporter: one(users, {
    fields: [contentFlags.reporterId],
    references: [users.id],
  }),
  reviewer: one(users, {
    fields: [contentFlags.reviewedBy],
    references: [users.id],
  }),
}));

export const interactionRatingsRelations = relations(interactionRatings, ({ one }) => ({
  message: one(messages, {
    fields: [interactionRatings.messageId],
    references: [messages.id],
  }),
  user: one(users, {
    fields: [interactionRatings.userId],
    references: [users.id],
  }),
  agent: one(users, {
    fields: [interactionRatings.agentId],
    references: [users.id],
  }),
}));

export const agentInteractionsRelations = relations(agentInteractions, ({ one }) => ({
  caller: one(users, {
    fields: [agentInteractions.callerAgentId],
    references: [users.id],
  }),
  callee: one(users, {
    fields: [agentInteractions.calleeAgentId],
    references: [users.id],
  }),
  token: one(paymentTokens, {
    fields: [agentInteractions.tokenId],
    references: [paymentTokens.id],
  }),
}));

export const agentReputationDomainsRelations = relations(agentReputationDomains, ({ one }) => ({
  agent: one(users, {
    fields: [agentReputationDomains.agentId],
    references: [users.id],
  }),
}));

// User Interactions (single home for engagement, journey, attribution, error events)
//
// targetType is intentionally `text`, not an enum — the set grew to include
// 'journey', 'attribution', and 'error' alongside the original recommender
// targets ('post', 'user', 'channel', 'tag'). targetId is `text` (not `uuid`)
// because non-uuid targets like 'sign_up', '/u/foo', utm sources, and stack
// hashes need to coexist with uuid post/user ids in the same column.
//
// Migration 0012 carries the uuid → text cast and the schema additions.
export const userInteractions = pgTable('user_interactions', {
  id: uuid('id').defaultRandom().primaryKey(),
  // Nullable so anonymous attribution / page_view / sign_up / error events can land
  // before the user has a row. Server-emitted rows for authenticated requests fill it.
  userId: uuid('user_id').references(() => users.id),
  targetType: text('target_type').notNull(), // 'post' | 'user' | 'channel' | 'tag' | 'journey' | 'attribution' | 'error' | 'message'
  targetId: text('target_id').notNull(),
  actionType: text('action_type').notNull(),
  // Stable client-generated UUID (rb_anon_id cookie) for stitching pre-signup → post-signup behavior.
  // Required as the join key on every analytics report that needs anon journey data.
  anonId: text('anon_id'),
  // Per-tab session id (rb_session_id sessionStorage) for events/session aggregations.
  // No dedicated index — sessions reports are batch CLI; in-memory GROUP BY is fine.
  sessionId: text('session_id'),
  // Server-derived request metadata (filled by /api/track from request headers).
  // `ip` is the peer the infrastructure vouched for, resolved by
  // lib/http/client-ip.ts rather than taken from the first hop of
  // x-forwarded-for (S-054, 2026-09-12: the first element is whatever the
  // caller wrote, so every value stored here before that fix was
  // client-chosen, and anything keyed on it, per-IP analytics or a block
  // list, was reading the attacker's own input). Falls back to x-real-ip;
  // `country` mirrors the x-geo-country Prometheus label; `userAgent` is the raw UA string.
  ip: text('ip'),
  country: text('country'),
  userAgent: text('user_agent'),
  metadata: jsonb('metadata'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('user_interactions_user_idx').on(table.userId),
  targetIdx: index('user_interactions_target_idx').on(table.targetType, table.targetId),
  // Composite (target_type, action_type, created_at) covers funnel scans on anonymous
  // rows that can't use seenIdx (whose leading column is user_id).
  targetActionCreatedIdx: index('user_interactions_target_action_created_idx').on(
    table.targetType, table.actionType, table.createdAt,
  ),
  anonIdx: index('user_interactions_anon_idx').on(table.anonId),
  createdAtIdx: index('user_interactions_created_idx').on(table.createdAt),
  seenIdx: index('user_interactions_seen_idx').on(table.userId, table.targetType, table.actionType, table.createdAt),
}));

// Agent Domains (for AOAuth rate limiting)
export const agentDomains = pgTable('agent_domains', {
  id: uuid('id').defaultRandom().primaryKey(),
  domain: text('domain').unique().notNull(), // e.g., "agents.example.com"
  tier: text('tier').default('free').notNull(), // 'free', 'verified', 'partner', 'unlimited'
  isBlocked: boolean('is_blocked').default(false),
  blockedReason: text('blocked_reason'),
  notes: text('notes'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// API Endpoint Rate Limits (per domain, per endpoint)
export const apiEndpointLimits = pgTable('api_endpoint_limits', {
  id: uuid('id').defaultRandom().primaryKey(),
  domain: text('domain').notNull(), // "*" for defaults, or specific domain
  endpoint: text('endpoint').notNull(), // e.g., "/discovery/feed", "/channels/*/post"
  perMinute: integer('per_minute'),
  perHour: integer('per_hour'),
  perDay: integer('per_day'),
  isBlocked: boolean('is_blocked').default(false),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  domainEndpointIdx: uniqueIndex('api_endpoint_limits_domain_endpoint_idx').on(table.domain, table.endpoint),
}));

// =====================
// OAuth 2.0 Server (for MCP client auth via Claude/ChatGPT)
// =====================

export const oauthClients = pgTable('oauth_clients', {
  id: uuid('id').defaultRandom().primaryKey(),
  clientId: text('client_id').notNull().unique(),
  clientSecret: text('client_secret'), // null for public clients
  clientName: text('client_name').notNull(),
  logoUri: text('logo_uri'),
  redirectUris: text('redirect_uris').array().notNull(),
  grantTypes: text('grant_types').array().default(['authorization_code']),
  description: text('description'),
  userId: uuid('user_id').references(() => users.id), // owner (null for auto-registered MCP clients)
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

export const oauthCodes = pgTable('oauth_codes', {
  id: uuid('id').defaultRandom().primaryKey(),
  code: text('code').notNull().unique(),
  clientId: text('client_id').notNull(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  redirectUri: text('redirect_uri').notNull(),
  codeChallenge: text('code_challenge').notNull(),
  codeChallengeMethod: text('code_challenge_method').default('S256').notNull(),
  scopes: text('scopes').array().default([]),
  expiresAt: timestamp('expires_at').notNull(),
  used: boolean('used').default(false).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// oauthTokens removed — replaced by accessTokens (type='oauth_app')

// ---------------------------------------------------------------------------
// Ranking & ML Training
// ---------------------------------------------------------------------------

/** Ranking logs for ML training data collection */
export const rankingLogs = pgTable('ranking_logs', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  postId: uuid('post_id').references(() => posts.id).notNull(),
  /** Full PostFeatureVector as JSON */
  features: jsonb('features').notNull(),
  /** Score assigned by the scorer */
  score: decimal('score', { precision: 12, scale: 6 }).notNull(),
  /** Position in the ranked feed (0-indexed) */
  position: integer('position').notNull(),
  /** Which scorer produced this ('heuristic', 'lightgbm', etc.) */
  scorerType: text('scorer_type').default('heuristic').notNull(),
  /** Request ID to group all posts from one feed request */
  requestId: text('request_id').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  userIdx: index('ranking_logs_user_idx').on(table.userId),
  postIdx: index('ranking_logs_post_idx').on(table.postId),
  requestIdx: index('ranking_logs_request_idx').on(table.requestId),
  createdAtIdx: index('ranking_logs_created_at_idx').on(table.createdAt),
}));

// ---------------------------------------------------------------------------
// X API Cache & Sync
// ---------------------------------------------------------------------------

/** Cached data from GetXAPI / X API */
export const xApiCache = pgTable('x_api_cache', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  /** Endpoint that produced this data (e.g., 'user/following', 'user/tweets') */
  endpoint: text('endpoint').notNull(),
  /** Raw response data as JSON */
  data: jsonb('data').notNull(),
  /** Cursor for pagination (null = first page) */
  cursor: text('cursor'),
  fetchedAt: timestamp('fetched_at').defaultNow().notNull(),
}, (table) => ({
  userEndpointIdx: index('x_api_cache_user_endpoint_idx').on(table.userId, table.endpoint),
}));

/** Tracks sync status per user per endpoint */
export const xSyncStatus = pgTable('x_sync_status', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id).notNull(),
  endpoint: text('endpoint').notNull(),
  lastSyncAt: timestamp('last_sync_at'),
  /** Number of pages fetched in last sync */
  pagesFetched: integer('pages_fetched').default(0),
  /** Last cursor for incremental sync */
  lastCursor: text('last_cursor'),
  status: text('status').default('idle'), // 'idle', 'syncing', 'done', 'error'
  error: text('error'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  userEndpointIdx: uniqueIndex('x_sync_status_user_endpoint_idx').on(table.userId, table.endpoint),
}));

// ---------------------------------------------------------------------------
// Connected Accounts & Agent Integrations
// ---------------------------------------------------------------------------

export const connectedAccounts = pgTable('connected_accounts', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  provider: text('provider').notNull(),
  authType: text('auth_type').notNull(), // 'oauth2' | 'api_key' | 'login_widget'
  providerUserId: text('provider_user_id'),
  providerUsername: text('provider_username'),
  accessToken: text('access_token'), // encrypted
  refreshToken: text('refresh_token'), // encrypted
  apiKey: text('api_key'), // encrypted, for API-key providers
  encryptionKeyVersion: integer('encryption_key_version').default(1),
  tokenExpiresAt: timestamp('token_expires_at'),
  scopes: text('scopes').array(),
  metadata: jsonb('metadata'), // provider-specific (instance URL, etc.)
  needsReauth: boolean('needs_reauth').default(false),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  userProviderProviderUserUniq: unique('connected_accounts_user_provider_provider_user_id_unique').on(
    table.userId,
    table.provider,
    table.providerUserId,
  ),
  userIdx: index('connected_accounts_user_idx').on(table.userId),
  providerIdx: index('connected_accounts_provider_idx').on(table.provider),
  providerUserIdx: index('connected_accounts_provider_user_idx').on(table.provider, table.providerUserId),
}));

export const agentIntegrations = pgTable('agent_integrations', {
  id: uuid('id').defaultRandom().primaryKey(),
  agentConfigId: uuid('agent_config_id').references(() => agentConfigs.id, { onDelete: 'cascade' }).notNull(),
  type: text('type').notNull(), // 'platform' | 'custom_mcp'
  provider: text('provider'), // for platform type
  name: text('name'), // for custom MCP, user-given name
  mcpServerUrl: text('mcp_server_url'), // for custom MCP
  mcpConfig: jsonb('mcp_config'), // for custom MCP: OAuth2 config, headers
  enabledCapabilities: text('enabled_capabilities').array(),
  isEnabled: boolean('is_enabled').default(true),
  /**
   * Per-agent binding to a specific connected account row.
   * Replaces the legacy "owner pool" lookup (which silently picked the first
   * matching connected_accounts row for the agent's owner). The migration
   * 0002 backfills this column from `metadata.botId` / `metadata.pageId`
   * pinning so that production agents do not change behaviour at upgrade.
   */
  connectedAccountId: uuid('connected_account_id').references(() => connectedAccounts.id, { onDelete: 'set null' }),
  metadata: jsonb('metadata').default({}).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  agentConfigIdx: index('agent_integrations_config_idx').on(table.agentConfigId),
  providerIdx: index('agent_integrations_provider_idx').on(table.provider),
  connectedAccountIdx: index('agent_integrations_connected_account_idx').on(table.connectedAccountId),
}));

export const connectedAccountsRelations = relations(connectedAccounts, ({ one }) => ({
  user: one(users, {
    fields: [connectedAccounts.userId],
    references: [users.id],
  }),
}));

export const agentIntegrationsRelations = relations(agentIntegrations, ({ one }) => ({
  agentConfig: one(agentConfigs, {
    fields: [agentIntegrations.agentConfigId],
    references: [agentConfigs.id],
  }),
  connectedAccount: one(connectedAccounts, {
    fields: [agentIntegrations.connectedAccountId],
    references: [connectedAccounts.id],
  }),
}));

// ==========================================
// AGENT MEMORY SYSTEM
// ==========================================

export const memory = pgTable('memory', {
  id: uuid('id').defaultRandom().primaryKey(),
  storeId: uuid('store_id').notNull(),
  ownerId: uuid('owner_id').notNull(),
  namespace: text('namespace').default('').notNull(),
  key: text('key').notNull(),
  value: jsonb('value'),
  inContext: boolean('in_context').default(true).notNull(),
  encrypted: boolean('encrypted').default(false).notNull(),
  /**
   * Server-side encryption indicator. When true, `value` holds JWE
   * ciphertext written via `encryptToken` (see `lib/auth/encryption.ts`)
   * and is decrypted only in trusted server contexts.
   *
   * Distinct from `encrypted` (which marks client-side opaque blobs).
   * Used by the `fn-secret:<functionName>` namespace for user-authored
   * function secrets — see `webagents/docs/skills/platform/memory.md`.
   */
  serverEncrypted: boolean('server_encrypted').default(false).notNull(),
  ttl: integer('ttl').default(0),
  expiresAt: timestamp('expires_at'),
  // GENERATED ALWAYS from `key + value::text`. The previous plain `tsvector`
  // column was never populated by writes, so the FTS leg of hybrid search in
  // `app/api/storage/memory/.../route.ts` returned empty. Mirrors the
  // `posts.search_vector` pattern above.
  /**
   * ADR-0026: generated ONLY for in-context rows (real agent memories, the
   * corpus memory-service hybrid search reads). App KV rows are written
   * inContext:false by construction and stopped paying this tsvector+GIN
   * tax on every write; their search runs over the DECLARED `fts`/`s1`
   * columns below instead.
   */
  searchVector: tsvector('search_vector').generatedAlwaysAs(
    (): ReturnType<typeof sql> => sql`case when ${memory.inContext} then to_tsvector('english', coalesce(${memory.key}, '') || ' ' || coalesce(${memory.value}::text, '')) else null end`
  ),
  /**
   * ADR-0026 declared search columns, platform-extracted like the slots:
   * `fts` from declared paths (input capped ~4KB - the document firewall),
   * `s1` the ONE trigram-searchable name-like field per namespace. Partial
   * compound GINs below keep both partition-local.
   */
  fts: tsvector('fts'),
  s1: text('s1'),
  /**
   * ADR-0026 index slots: typed, nullable, PLATFORM-extracted columns whose
   * meaning an app declares per key-prefix (seed-trusted registry row,
   * `lib/agents/kv-index.ts`). Rows in undeclared namespaces stay NULL and
   * cost nothing; the partial indexes below serve `kv.query` range and
   * equality lookups so no query ever degrades into a scan. Never map
   * update-hot fields (counters) into slots — churn defeats HOT updates.
   */
  k1: text('k1'),
  k2: text('k2'),
  n1: decimal('n1'),
  t1: timestamp('t1'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  storeIdx: index('memory_store_idx').on(table.storeId),
  storeOwnerNsKeyIdx: uniqueIndex('memory_store_owner_ns_key_idx').on(
    table.storeId, table.ownerId, table.namespace, table.key,
  ),
  // kv.list in COLLATE "C" (0059): locale collation cannot range-scan
  // `key LIKE 'prefix%'`, so listing a prefix that sorts behind a large
  // key family walked the whole partition (30s statement timeouts at 1M
  // rows). Keys are identifiers; byte order is their order.
  storeNsKeyCIdx: index('memory_store_ns_key_c_idx').on(
    table.storeId, table.namespace, sql`${table.key} COLLATE "C"`,
  ),
  // fn-host KV hot path: get/put/delete resolve (store, ns, key) and list
  // range-scans (store, ns, key-prefix) WITHOUT owner_id, so the unique
  // upsert index above can't serve them past its store_id prefix (one agent
  // store holds agent memory + every app/deck KV row). Migration 0044.
  storeNsKeyIdx: index('memory_store_ns_key_idx').on(
    table.storeId, table.namespace, table.key,
  ),
  expiresAtIdx: index('memory_expires_at_idx')
    .on(table.expiresAt)
    .where(sql`${table.expiresAt} IS NOT NULL`),
  searchVectorIdx: index('memory_search_vector_idx')
    .using('gin', table.searchVector),
  // ADR-0026: partition-leading composites, partial over non-null slots so
  // undeclared rows never pay. (store, ns, k1, t1) serves equality plus
  // range in one walk (due-by-stage); (store, ns, t1) serves pure due-time
  // queues.
  // k1 in COLLATE "C" (0058): slot values are identifiers, and under the
  // database's en_US.utf8 collation `LIKE 'prefix%'` cannot use a plain
  // btree at all — byte order makes prefix ops and ORDER BY k1 index-served.
  slotK1T1Idx: index('memory_slot_k1_t1_idx')
    .on(table.storeId, table.namespace, sql`${table.k1} COLLATE "C"`, table.t1)
    .where(sql`${table.k1} IS NOT NULL`),
  slotT1Idx: index('memory_slot_t1_idx')
    .on(table.storeId, table.namespace, table.t1)
    .where(sql`${table.t1} IS NOT NULL`),
  // ADR-0026 declared search: partition-local FTS and trigram (btree_gin
  // carries the uuid/text prefix columns inside the GIN).
  ftsIdx: index('memory_fts_idx')
    .using('gin', table.storeId, table.namespace, table.fts)
    .where(sql`${table.fts} IS NOT NULL`),
  s1TrgmIdx: index('memory_s1_trgm_idx')
    .using('gin', table.storeId, table.namespace, sql`${table.s1} gin_trgm_ops`)
    .where(sql`${table.s1} IS NOT NULL`),
}));

/**
 * Per-namespace usage accounting for the `memory` table (Phase 0b).
 *
 * Maintained INCREMENTALLY (O(1)) on every put / delete / incr so quota
 * enforcement never scans the whole store (the old widget-kv route summed
 * every row per write; `memory-service` ran COUNT(*) per write). One row
 * per (storeId, ownerId, namespace) tracks the serialized byte total and
 * the live key count. Caps resolve from the owner's plan
 * (`lib/plans/memory-limits.ts`); the cap-check is a single-row read.
 */
export const memoryUsage = pgTable('memory_usage', {
  id: uuid('id').defaultRandom().primaryKey(),
  storeId: uuid('store_id').notNull(),
  ownerId: uuid('owner_id').notNull(),
  namespace: text('namespace').default('').notNull(),
  totalBytes: bigint('total_bytes', { mode: 'bigint' }).default(0n).notNull(),
  keyCount: integer('key_count').default(0).notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  storeOwnerNsIdx: uniqueIndex('memory_usage_store_owner_ns_idx').on(
    table.storeId, table.ownerId, table.namespace,
  ),
}));

export const memoryGrants = pgTable('memory_grants', {
  storeId: uuid('store_id').notNull(),
  agentId: uuid('agent_id').notNull(),
  level: memoryGrantLevelEnum('level').default('read').notNull(),
  grantedBy: uuid('granted_by').notNull(),
  expiresAt: timestamp('expires_at'),
  metadata: jsonb('metadata'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  pk: uniqueIndex('memory_grants_pk').on(table.storeId, table.agentId),
  agentIdx: index('memory_grants_agent_idx').on(table.agentId),
  expiresAtIdx: index('memory_grants_expires_at_idx')
    .on(table.expiresAt)
    .where(sql`${table.expiresAt} IS NOT NULL`),
}));

/**
 * Retention per namespace of the SDK memory skill's portal tier (migration
 * 0092, gap-closure plan item 2.1, 2026-09-26). The entries themselves are
 * `memory` rows under the `wa:` namespace family, and so is the sync: the
 * cursor is their `updated_at` and a forgotten entry is a content-free
 * tombstone row with `expires_at` set (`lib/storage/memory-scoped-service.ts`
 * says why). This row carries the one thing an entry cannot, the retention
 * the owner set on a namespace, and exists only while one is set: "keep
 * forever" deletes it. A `caller:` namespace's principal is its own suffix.
 */
export const memoryNamespaces = pgTable('memory_namespaces', {
  storeId: uuid('store_id').notNull(),
  namespace: text('namespace').notNull(),
  retentionDays: integer('retention_days').notNull(),
}, (table) => ({
  pk: primaryKey({ name: 'memory_namespaces_pkey', columns: [table.storeId, table.namespace] }),
}));

export type ChannelSenderStatus = 'pending' | 'approved' | 'blocked';

/**
 * The channel relay's sender ledger (migration 0093, gap-closure plan item
 * 2.2, 2026-09-26): one row per (agent, channel type, sender id) for every
 * sender that wrote to an agent over a connected channel. `status` is the
 * owner's decision (`pending` until approved, or `approved` at once under an
 * `open` sender policy), `pairing_code` what the held sender was shown, and
 * the counts are the per-sender usage the owner sees. Read and written by
 * `lib/messaging/channel-relay.ts` only. The bot's owner is the agent's own
 * `users.owner_id`, never copied here.
 */
export const channelSenders = pgTable('channel_senders', {
  id: uuid('id').defaultRandom().primaryKey(),
  agentId: uuid('agent_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  /** The type an agent file names: `telegram`, `slack`, `email`, `sms`, ... (`lib/messaging/channel-identity.ts`). */
  channelType: text('channel_type').notNull(),
  senderId: text('sender_id').notNull(),
  displayName: text('display_name'),
  status: text('status').$type<ChannelSenderStatus>().default('pending').notNull(),
  pairingCode: text('pairing_code'),
  pairingCodeExpiresAt: timestamp('pairing_code_expires_at'),
  pairingNotifiedAt: timestamp('pairing_notified_at'),
  contactUserId: uuid('contact_user_id').references(() => users.id, { onDelete: 'set null' }),
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'set null' }),
  messageCount: integer('message_count').default(0).notNull(),
  heldCount: integer('held_count').default(0).notNull(),
  turnCount: integer('turn_count').default(0).notNull(),
  firstSeenAt: timestamp('first_seen_at').defaultNow().notNull(),
  lastSeenAt: timestamp('last_seen_at').defaultNow().notNull(),
  approvedAt: timestamp('approved_at'),
  approvedById: uuid('approved_by_id').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  agentTypeSenderUq: unique('channel_senders_agent_type_sender_uq').on(table.agentId, table.channelType, table.senderId),
  agentStatusIdx: index('channel_senders_agent_status_idx').on(table.agentId, table.status),
  statusCheck: check('channel_senders_status_check', sql`${table.status} IN ('pending', 'approved', 'blocked')`),
}));

/**
 * Function invocations audit / activity log.
 *
 * Every invocation of a user-authored function (cron / custom_http /
 * custom_tools / manual / function-to-function) writes one row. Used by:
 *   - Functions pane "Activity" tab (paginated history per function).
 *   - Replay action (loads original envelope to re-run via /invoke).
 *   - Authoring trail (kind='authoring' rows record function declares /
 *     skill use changes — see §20 of the plan).
 *
 * The table is partitioned by `started_at` (range; daily partitions);
 * the partition management cronjob lives in
 * `infrastructure/applications/portal/base/cronjobs/`.
 *
 * Default retention is 30 days; tier-driven via the partition pruner.
 */
export const functionInvocations = pgTable('function_invocations', {
  id: uuid('id').defaultRandom().primaryKey(),
  agentId: uuid('agent_id').notNull(),
  /** agent_configs.functions key (stable across content revisions). */
  functionName: text('function_name').notNull(),
  /** content row id at time of invocation. */
  contentRef: uuid('content_ref').notNull(),
  /** cron | custom_http | custom_tools | manual | function | authoring */
  sourceSkill: text('source_skill').notNull(),
  /** consuming-skill entry id (cron schedule id, http endpoint id, tool id). */
  consumerId: text('consumer_id').notNull(),
  /** caller user id when known (owner POSTs / authoring trail / sessions). */
  callerUserId: uuid('caller_user_id'),
  /** factory | host | ui | cli — only set for `sourceSkill='authoring'` rows. */
  surface: text('surface'),
  startedAt: timestamp('started_at').defaultNow().notNull(),
  durationMs: integer('duration_ms').notNull(),
  cpuMs: integer('cpu_ms').notNull().default(0),
  ingressBytes: bigint('ingress_bytes', { mode: 'bigint' }).notNull().default(0n),
  egressBytes: bigint('egress_bytes', { mode: 'bigint' }).notNull().default(0n),
  /** ok | error | timeout | quota_exceeded */
  status: text('status').notNull(),
  errorCode: text('error_code'),
  errorMessage: text('error_message'),
  /** Pass-through metering for billing reconciliation. */
  metering: jsonb('metering'),
  /** Authoring-only — JSON diff before/after for the audit trail. */
  authoringDiff: jsonb('authoring_diff'),
  /** W3C traceparent for cross-service correlation (portal/executor logs). */
  traceparent: text('traceparent'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  agentStartedIdx: index('function_invocations_agent_started_idx').on(
    table.agentId, table.startedAt,
  ),
  agentFnStartedIdx: index('function_invocations_agent_fn_started_idx').on(
    table.agentId, table.functionName, table.startedAt,
  ),
}));

// =============================================================================
// Workspaces are CONTENT FOLDERS now (unified model). The legacy `workspaces`,
// `workspace_members`, and `workspace_items` tables are RETIRED (migration
// 0038_drop_workspace_tables): a workspace IS a content folder
// (`metadata.projectMime`), its canvas lives in a workspace-level Y.Doc
// (`content_room_docs`), and ACL is `content_links` (Reader/Editor/Owner). The
// `Workspace` / `WorkspaceItem` TYPES now live in lib/workspaces/types.ts.
// =============================================================================

// ---------------------------------------------------------------------------
// Collab room metadata (Hocuspocus-managed Yjs rooms).
//
// Per ADR-v3-05/06: room state is persisted per-item on
// `workspace_items.yjs_state` (or, for workspace-wide canvases,
// keyed by scope='workspace'). This table holds room-level metadata
// only — auth flows through the existing workspace ACL at the
// Hocuspocus auth callback layer, no separate room_membership table.
// ---------------------------------------------------------------------------

export const collabRoomScopeEnum = ['workspace', 'item'] as const;
export type CollabRoomScope = (typeof collabRoomScopeEnum)[number];

export const collabRooms = pgTable('collab_rooms', {
  id: uuid('id').defaultRandom().primaryKey(),
  scope: text('scope').notNull(),
  scopeId: uuid('scope_id').notNull(),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
  deletedAt: timestamp('deleted_at', { withTimezone: true }),
}, (table) => ({
  scopeUnique: uniqueIndex('collab_rooms_scope_scope_id_idx')
    .on(table.scope, table.scopeId)
    .where(sql`${table.deletedAt} IS NULL`),
  updatedIdx: index('collab_rooms_updated_idx')
    .on(table.updatedAt)
    .where(sql`${table.deletedAt} IS NULL`),
  scopeCheck: check('collab_rooms_scope_check', sql`${table.scope} IN ('workspace', 'item')`),
}));

// ---------------------------------------------------------------------------
// Collab room docs — durable Yjs state for code-based lobby rooms.
//
// `room`-scoped collab docs (collab-board / robongus lobbies) key on
// `<contentId>:<roomCode>` — NOT a workspace_item id, and the `/w/` share path
// has no workspace_item — so they can't live on `workspace_items`, nor in
// `collab_rooms` (whose `scope_id uuid` + CHECK exclude room). This table
// mirrors the item-scope yjs columns (bytea state + state_vector + jsonb
// snapshot recovery seed) keyed by the text room key. See
// apps/collab/persistence.ts (room load/store branches).
// ---------------------------------------------------------------------------

export const collabRoomDocs = pgTable('collab_room_docs', {
  id: uuid('id').defaultRandom().primaryKey(),
  // `<contentId>:<roomCode>` — the Hocuspocus room scopeId (text, not uuid).
  roomKey: text('room_key').notNull(),
  // `yjsState` holds the encoded Y.Doc update log; `yjsStateVector` the state
  // vector for delta sync; `stateSnapshot` the recovery seed if the bytea
  // won't decode (mirrors workspace_items, ADR-v3-05/06).
  yjsState: bytea('yjs_state'),
  yjsStateVector: bytea('yjs_state_vector'),
  stateSnapshot: jsonb('state_snapshot').notNull().default(sql`'{}'::jsonb`),
  yjsUpdatedAt: timestamp('yjs_updated_at', { withTimezone: true }),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
  deletedAt: timestamp('deleted_at', { withTimezone: true }),
}, (table) => ({
  roomKeyUnique: uniqueIndex('collab_room_docs_room_key_idx')
    .on(table.roomKey)
    .where(sql`${table.deletedAt} IS NULL`),
  updatedIdx: index('collab_room_docs_updated_idx')
    .on(table.updatedAt)
    .where(sql`${table.deletedAt} IS NULL`),
}));

// ---------------------------------------------------------------------------
// Content room docs — durable Yjs state for a CONTENT FOLDER's collaborative
// doc. The unified workspace model makes a workspace a content folder whose
// canvas (the item instances + camera) lives in a workspace-level Y.Doc; this
// table is that doc's authoritative home, keyed by the folder's content id with
// an ON DELETE CASCADE so deleting the workspace folder reclaims its doc.
//
// Same bytea-state + state-vector + jsonb-snapshot shape as workspace_items /
// collab_room_docs (ADR-v3-05/06): `stateSnapshot` is the SSR-readable +
// recovery seed. Room name is `workspace:<folderId>` (scope='workspace',
// scopeId=folderId). See apps/collab/persistence.ts (workspace load/store).
// ---------------------------------------------------------------------------

export const contentRoomDocs = pgTable('content_room_docs', {
  // The collaborative doc is 1:1 with the content folder it belongs to.
  contentId: uuid('content_id').primaryKey().references(() => content.id, { onDelete: 'cascade' }),
  yjsState: bytea('yjs_state'),
  yjsStateVector: bytea('yjs_state_vector'),
  stateSnapshot: jsonb('state_snapshot').notNull().default(sql`'{}'::jsonb`),
  yjsUpdatedAt: timestamp('yjs_updated_at', { withTimezone: true }),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  updatedIdx: index('content_room_docs_updated_idx').on(table.updatedAt),
}));

// ---------------------------------------------------------------------------
// Canvas comments now live in the workspace Y.Doc (content_room_docs) as
// `commentThreads` / `commentMessages` maps — threads, replies AND emoji
// reactions sync + persist with the canvas itself, like the vector widget.
// The old Postgres-backed tables (canvas_comment_threads / canvas_comments)
// are dropped in migration 0039.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// SSH credentials & terminal sessions
//
// Encrypted SSH key vault for the workspace `terminal` node. Reuses the
// platform JWE crypto (lib/auth/encryption.ts) but lives in a dedicated
// table so it is per-user-namespaced — see migration 0017_terminal.sql for
// the rationale (audit found cross-user collisions if reusing
// connected_accounts due to its global SSO partial unique index).
// ---------------------------------------------------------------------------

export const sshCredentials = pgTable('ssh_credentials', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').notNull().references(() => users.id, { onDelete: 'cascade' }),
  label: text('label').notNull(),
  // 'key' = key only; 'key_with_passphrase' = key + passphrase.
  // One-time password connections are NOT persisted here.
  authType: text('auth_type').notNull(),
  // OpenSSH wire format, base64.
  publicKey: text('public_key').notNull(),
  // SHA-256 fingerprint (lowercase hex). Per-user uniqueness key.
  fingerprint: text('fingerprint').notNull(),
  comment: text('comment'),
  // JWE of {"privateKey":"...","passphrase":"..."} via lib/auth/encryption.ts.
  secretJwe: text('secret_jwe').notNull(),
  // [{ host, port, hostKeyFingerprint, hostKeyType, trustedAt }]
  knownHosts: jsonb('known_hosts').notNull().default(sql`'[]'::jsonb`),
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
  lastUsedAt: timestamp('last_used_at', { withTimezone: true }),
}, (table) => ({
  authTypeCheck: check(
    'ssh_credentials_auth_type_check',
    sql`${table.authType} IN ('key', 'key_with_passphrase')`,
  ),
  userIdx: index('ssh_credentials_user_idx').on(table.userId),
  userFingerprintUniq: uniqueIndex('ssh_credentials_user_fingerprint_uniq').on(
    table.userId, table.fingerprint,
  ),
}));

export const terminalSessions = pgTable('terminal_sessions', {
  id: uuid('id').defaultRandom().primaryKey(),
  userId: uuid('user_id').notNull().references(() => users.id, { onDelete: 'cascade' }),
  workspaceId: uuid('workspace_id').notNull(),
  itemId: uuid('item_id').notNull(),
  // 'webagentsd' = local PTY via Tauri relay; 'ssh' = persisted credential;
  // 'ssh-password' = one-time password.
  targetKind: text('target_kind').notNull(),
  // 'user@host:port' or 'peer:<id>' — for log views, no secret material.
  targetSummary: text('target_summary').notNull(),
  sshCredentialId: uuid('ssh_credential_id').references(() => sshCredentials.id, { onDelete: 'set null' }),
  connectedAt: timestamp('connected_at', { withTimezone: true }).defaultNow().notNull(),
  disconnectedAt: timestamp('disconnected_at', { withTimezone: true }),
  bytesIn: bigint('bytes_in', { mode: 'bigint' }).notNull().default(BigInt(0)),
  bytesOut: bigint('bytes_out', { mode: 'bigint' }).notNull().default(BigInt(0)),
  exitCode: integer('exit_code'),
  closeReason: text('close_reason'),
}, (table) => ({
  targetKindCheck: check(
    'terminal_sessions_target_kind_check',
    sql`${table.targetKind} IN ('ssh', 'ssh-password', 'webagentsd')`,
  ),
  userIdx: index('terminal_sessions_user_idx').on(table.userId),
  itemIdx: index('terminal_sessions_item_idx').on(table.itemId),
  userConnectedIdx: index('terminal_sessions_user_connected_idx').on(
    table.userId, table.connectedAt,
  ),
}));

// Type exports
export type User = InferSelectModel<typeof users>;
export type Channel = InferSelectModel<typeof channels>;
export type ChannelCategory = InferSelectModel<typeof channelCategories>;
export type Post = InferSelectModel<typeof posts>;
export type Comment = InferSelectModel<typeof comments>;
export type Tag = InferSelectModel<typeof tags>;
export type Chat = InferSelectModel<typeof chats>;
export type ChatParticipant = InferSelectModel<typeof chatParticipants>;
export type Message = InferSelectModel<typeof messages>;
export type Content = InferSelectModel<typeof content>;
export type ContentLink = InferSelectModel<typeof contentLinks>;
export type PushSubscription = InferSelectModel<typeof pushSubscriptions>;
export type UserInteraction = InferSelectModel<typeof userInteractions>;
export type AgentDomain = InferSelectModel<typeof agentDomains>;
export type ApiEndpointLimit = InferSelectModel<typeof apiEndpointLimits>;
export type ChannelPermission = InferSelectModel<typeof channelPermissions>;
export type ReservedPrefix = InferSelectModel<typeof reservedPrefixes>;
export type DomainVerification = InferSelectModel<typeof domainVerifications>;
export type DomainAppBinding = InferSelectModel<typeof domainAppBindings>;
export type ChannelTag = InferSelectModel<typeof channelTags>;
export type TLD = InferSelectModel<typeof tlds>;
export type ChannelAlias = InferSelectModel<typeof channelAliases>;
export type ReferralCode = InferSelectModel<typeof referralCodes>;
export type Referral = InferSelectModel<typeof referrals>;
export type DeckShare = InferSelectModel<typeof deckShares>;
export type AccessToken = InferSelectModel<typeof accessTokens>;
export type AgentConfig = InferSelectModel<typeof agentConfigs>;
export type BalanceTransaction = InferSelectModel<typeof balanceTransactions>;
export type PaymentToken = InferSelectModel<typeof paymentTokens>;
export type PaymentCharge = InferSelectModel<typeof paymentCharges>;
export type PaymentLock = InferSelectModel<typeof paymentLocks>;
export type SpendingOverride = InferSelectModel<typeof spendingOverrides>;
export type Repost = InferSelectModel<typeof reposts>;
export type PinnedPost = InferSelectModel<typeof pinnedPosts>;
export type SavedPost = InferSelectModel<typeof savedPosts>;
export type PostSubscription = InferSelectModel<typeof postSubscriptions>;
export type OAuthClient = InferSelectModel<typeof oauthClients>;
export type OAuthCode = InferSelectModel<typeof oauthCodes>;
export type RankingLog = InferSelectModel<typeof rankingLogs>;
export type XApiCache = InferSelectModel<typeof xApiCache>;
export type XSyncStatus = InferSelectModel<typeof xSyncStatus>;
export type ConnectedAccount = InferSelectModel<typeof connectedAccounts>;
export type AgentIntegration = InferSelectModel<typeof agentIntegrations>;
export type ContentFlag = InferSelectModel<typeof contentFlags>;
export type InteractionRating = InferSelectModel<typeof interactionRatings>;
export type AgentInteraction = InferSelectModel<typeof agentInteractions>;
export type AgentReputationDomain = InferSelectModel<typeof agentReputationDomains>;
export type Memory = InferSelectModel<typeof memory>;
export type MemoryGrant = InferSelectModel<typeof memoryGrants>;
export type MemoryNamespace = InferSelectModel<typeof memoryNamespaces>;
export type ChannelSender = InferSelectModel<typeof channelSenders>;
// Workspace / WorkspaceItem types live in lib/workspaces/types.ts now (the
// backing tables are retired — a workspace is a content folder).
export type SshCredential = InferSelectModel<typeof sshCredentials>;
export type TerminalSession = InferSelectModel<typeof terminalSessions>;

// ── Transactional email (migration 0049) ─────────────────────────────────────
//
// See docs/internal/launch/transactional-email-spec.md. Sending is queued, never
// inline: a route writes an email_messages row and returns, and a cron drains it.
// A SendGrid timeout must never fail a mark-paid transaction, so the worst failure
// mode here is a delayed email, never a lost payout.

/**
 * Outbound message log. Every send attempt is a row, including the ones that were
 * suppressed or deferred, so "why did this person not get the mail" is answerable
 * from the table alone.
 *
 * `idempotencyKey` is the logical event id (`wd-paid:<requestId>`,
 * `grant:<grantId>`, `terms:<version>:<userId>`); its unique index is the
 * database-level backstop against a double-send. A deliberate admin resend mints
 * a NEW key so the resend is auditable rather than silent.
 *
 * Status ladder: queued | deferred (over the hourly cap) | suppressed → sent →
 * delivered | bounced | complained, or failed after the retry budget. Statuses
 * after `sent` are written by the provider webhook.
 */
export const emailMessages = pgTable('email_messages', {
  id: uuid('id').defaultRandom().primaryKey(),
  // Nullable: some sends go to an address with no account behind it.
  userId: uuid('user_id').references(() => users.id),
  toEmail: text('to_email').notNull(),
  template: text('template').notNull(),
  // 'money' | 'legal' | 'product'. Money and legal are not opt-out-able.
  category: text('category').default('product').notNull(),
  subject: text('subject').notNull(),
  // 'opted_out' and 'expired' arrived with migration 0052: an opt-out is not a
  // deliverability problem the way a suppression is, and a row that sat in the
  // queue past lib/email/policy.ts MAX_QUEUE_AGE_MS is closed out rather than
  // sent late.
  status: text('status').default('queued').notNull(),
  idempotencyKey: text('idempotency_key').notNull(),
  providerMessageId: text('provider_message_id'),
  error: text('error'),
  attempts: integer('attempts').default(0).notNull(),
  // The rendered template's props. Kept so the admin log can re-render exactly
  // what was sent without reaching back into the money tables.
  data: jsonb('data').default(sql`'{}'::jsonb`).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  sentAt: timestamp('sent_at'),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  statusCheck: check(
    'email_messages_status_check',
    sql`${table.status} IN ('queued', 'deferred', 'suppressed', 'opted_out', 'expired', 'sent', 'delivered', 'bounced', 'complained', 'failed')`,
  ),
  idempotencyKeyIdx: uniqueIndex('email_messages_idempotency_key_idx').on(table.idempotencyKey),
  userCreatedIdx: index('email_messages_user_created_idx').on(table.userId, table.createdAt),
  statusCreatedIdx: index('email_messages_status_created_idx').on(table.status, table.createdAt),
  toEmailIdx: index('email_messages_to_email_idx').on(table.toEmail, table.createdAt),
  providerMessageIdIdx: index('email_messages_provider_message_id_idx').on(table.providerMessageId),
}));

/**
 * Suppression list, keyed by ADDRESS rather than user id: a bounce is a property
 * of the mailbox, and the same address can outlive or precede an account.
 * A complaint is permanent; a bounce can be cleared by an admin once the mailbox
 * is fixed. `unsubscribed` only stops non-essential mail, never a payout receipt.
 */
export const emailSuppressions = pgTable('email_suppressions', {
  email: text('email').primaryKey(),
  reason: text('reason').notNull(),
  note: text('note'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  createdBy: uuid('created_by').references(() => users.id),
}, (table) => ({
  reasonCheck: check(
    'email_suppressions_reason_check',
    sql`${table.reason} IN ('unsubscribed', 'bounced', 'complained', 'manual')`,
  ),
  createdIdx: index('email_suppressions_created_idx').on(table.createdAt),
}));

export const emailMessagesRelations = relations(emailMessages, ({ one }) => ({
  user: one(users, { fields: [emailMessages.userId], references: [users.id] }),
}));

export const emailSuppressionsRelations = relations(emailSuppressions, ({ one }) => ({
  createdByUser: one(users, { fields: [emailSuppressions.createdBy], references: [users.id] }),
}));

export type EmailMessage = InferSelectModel<typeof emailMessages>;
export type EmailSuppression = InferSelectModel<typeof emailSuppressions>;
// ── Marketing campaigns (migration 0050) ─────────────────────────────────────
//
// The portal's own campaign tables lived here until 2026-08-07. They are
// gone with the rest of the platform marketing surface - the Reach app does
// campaigns now, in its own KV, under its own consent and suppression
// checks. Migration 0062 carries the platform's unsubscribe records into the
// app's partition and then drops the five tables.
//
// What stayed elsewhere: lib/email/consent.ts (the regional opt-in rule, now
// used to decide who the portal may PUSH to the app) and lib/crm/ (that push
// itself). `email_suppressions` is untouched - it is transactional
// deliverability, never campaign consent.

// Email invitations to a project (ADR-0036, migration 0077).
//
// WHY THIS TABLE EXISTS. An invitation has nowhere to be until the invitee is
// a principal: content_links.user_id is a hard FK to users.id and
// content_links_subject_check admits exactly one of user_id or chat_id, so a
// grant cannot be parked on an email address. The two shortcuts are both
// unsafe here. Relaxing the CHECK would push a third subject kind that can
// never authorize anyone into canAccessContent, the recursive ancestor CTEs,
// canModifyViaAncestor and listGrants. Pre-creating a users row for the
// address is a takeover primitive: there is no emailVerified column anywhere,
// PATCH /api/users/me lets any account set users.email to any unused string,
// and all three OAuth callbacks adopt an existing row on bare email equality.
//
// SO: AN INVITE IS A CLAIM TICKET, NOT AN ACCESS GRANT. No authorization
// predicate reads this table. A claim (POST /api/content/invites/claim, never a
// GET, because mail scanners and unfurlers fetch every link) exchanges the
// ticket for a principal and writes an ordinary content_links row with
// added_via = 'invite' and visibility = 'private'. The claimant is recorded
// here (claimed_by_user_id), not on content_links, so this feature adds no DDL
// to the hottest ACL table and revokeUsers can flip the invite by
// (content_id, user_id) whichever of its three callers removed the person.
//
// The ticket itself is never stored: code_hash is sha256 of a 22-char base62
// code from generateShareCode (about 131 bits). content_links.share_token is
// stored raw and a replica read yields live capabilities; an invite code
// cannot be recovered from this row. Revoke and re-invite both overwrite
// code_hash with a fresh random tombstone so no code ever addresses a dead
// row through any route.
//
// `email` is the exact normalised address the sharer typed and the only
// string ever mailed. `emailKey` folds plus-tags and provider dots and exists
// ONLY for caps: the pending unique index and the per-project and
// per-recipient limits. It is never used to address a message or to compare
// with users.email.
//
// Erasure is explicit, not by cascade: account deletion soft-deletes and
// scrubs (lib/account/deletion.ts), so purgeUserAccount deletes rows by
// invited_by and nulls the address on rows by claimed_by_user_id, and the
// guest-gc cron purges terminal rows after 30 days.
export const contentInvites = pgTable('content_invites', {
  id: uuid('id').defaultRandom().primaryKey(),
  contentId: uuid('content_id').references(() => content.id, { onDelete: 'cascade' }).notNull(),
  /** Routing label only. normalizeEmail form. Never an identity. */
  email: text('email').notNull(),
  /** Caps-only canonical mailbox key (plus-tag and dot folded). Never mailed. */
  emailKey: text('email_key').notNull(),
  /** Reuses the content_links vocabulary verbatim; CHECK narrows it to shared | editor. */
  role: contentLinkRoleEnum('role').default('shared').notNull(),
  invitedBy: uuid('invited_by').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  /** sha256 hex of the base62 ticket. The ticket is never persisted anywhere. */
  codeHash: text('code_hash').notNull(),
  /** pending | claimed | revoked | expired. A row never returns to pending. */
  status: text('status').default('pending').notNull(),
  expiresAt: timestamp('expires_at').notNull(),
  claimedAt: timestamp('claimed_at'),
  claimedByUserId: uuid('claimed_by_user_id').references(() => users.id, { onDelete: 'set null' }),
  /** First 12 hex of sha256(ip), same convention as /api/auth/guest. Never the raw address. */
  claimIpHash: text('claim_ip_hash'),
  claimUaHash: text('claim_ua_hash'),
  revokedAt: timestamp('revoked_at'),
  /** not_sent | queued | suppressed | failed. What the mail rail actually did; drives the dialog's honest line. */
  delivery: text('delivery').default('not_sent').notNull(),
  /** email_off | suppressed | opted_out | invalid | error, when delivery is not 'queued'. */
  deliveryReason: text('delivery_reason'),
  emailMessageId: uuid('email_message_id').references(() => emailMessages.id, { onDelete: 'set null' }),
  sendCount: integer('send_count').default(0).notNull(),
  lastSentAt: timestamp('last_sent_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  roleCheck: check('content_invites_role_check', sql`${table.role}::text IN ('shared', 'editor', 'suggester')`),
  statusCheck: check(
    'content_invites_status_check',
    sql`${table.status} IN ('pending', 'claimed', 'revoked', 'expired')`,
  ),
  deliveryCheck: check(
    'content_invites_delivery_check',
    sql`${table.delivery} IN ('not_sent', 'queued', 'suppressed', 'failed')`,
  ),
  claimCheck: check(
    'content_invites_claim_check',
    sql`${table.status} <> 'claimed' OR ${table.claimedAt} IS NOT NULL`,
  ),
  codeHashIdx: uniqueIndex('content_invites_code_hash_idx').on(table.codeHash),
  // One live ticket per mailbox per project; keyed on emailKey so plus
  // addressing cannot fan out.
  pendingIdx: uniqueIndex('content_invites_pending_idx')
    .on(table.contentId, table.emailKey)
    .where(sql`${table.status} = 'pending'`),
  contentIdx: index('content_invites_content_idx').on(table.contentId, sql`${table.createdAt} DESC`),
  inviterCreatedIdx: index('content_invites_inviter_created_idx').on(table.invitedBy, sql`${table.createdAt} DESC`),
  claimantIdx: index('content_invites_claimant_idx')
    .on(table.contentId, table.claimedByUserId)
    .where(sql`${table.claimedByUserId} IS NOT NULL`),
  statusExpiresIdx: index('content_invites_status_expires_idx').on(table.status, table.expiresAt),
}));

export const contentInvitesRelations = relations(contentInvites, ({ one }) => ({
  content: one(content, { fields: [contentInvites.contentId], references: [content.id] }),
  inviter: one(users, { fields: [contentInvites.invitedBy], references: [users.id] }),
  claimant: one(users, { fields: [contentInvites.claimedByUserId], references: [users.id] }),
  emailMessage: one(emailMessages, { fields: [contentInvites.emailMessageId], references: [emailMessages.id] }),
}));

export type ContentInvite = InferSelectModel<typeof contentInvites>;

// ---------------------------------------------------------------------------
// The conversation program (migration 0079): agent_turns, agent_turn_refusals,
// agent_addresses, inbox_contacts, inbox_contact_messages, inbox_blocks. Six
// brand new tables, every foreign key pointing outward or into another table
// of the same file, no authorization predicate reads any of them. The SQL and
// its reasoning are in lib/db/migrations/0079_agent_turns_and_inbox.sql; these
// declarations mirror it column for column, index for index and CHECK for
// CHECK so that a drizzle-kit diff stays empty. Unnamed inline CHECKs in the
// SQL carry Postgres's auto-name (<table>_<column>_check) here for the same
// reason. The unions below mirror each CHECK's vocabulary; they are wire and
// DB values.
// ---------------------------------------------------------------------------

/** Why the relay will not write to an address again. Recipient-driven only. */
export type InboxRelaySuppressionReason = 'complaint' | 'hard_bounce' | 'unsubscribed';

export type AgentTurnCauseKind = 'agent' | 'external';
export type AgentTurnTransport = 'portal' | 'smtp';
export type AgentTurnDeliveryPolicy = 'immediate' | 'debounce' | 'interval' | 'manual';
/** Two values. The design's `classify` posture joins the CHECK when M2-01 writes it. */
export type AgentTurnPosture = 'full' | 'restricted';
/** Every state a claimed or claimable row moves through. A refusal is not one: see agentTurnRefusals. */
export type AgentTurnState = 'unfunded' | 'due' | 'running' | 'done' | 'failed' | 'cancelled' | 'expired';

/**
 * One agent turn: the unit of dispatch and settlement for every agent turn
 * that was not caused by a person's own inline send. A queue table with an
 * archive: every hot index is partial on a transient state so terminal rows
 * fall out of what the dispatcher reads, and the sweep prunes them. A raise
 * that stores no turn writes `agentTurnRefusals`, never a row here.
 */
export const agentTurns = pgTable('agent_turns', {
  id: uuid('id').defaultRandom().primaryKey(),
  /** RESTRICT: a chats delete fails on a referenced row rather than orphaning ledger evidence. */
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'restrict' }).notNull(),
  agentId: uuid('agent_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  causeKind: text('cause_kind').$type<AgentTurnCauseKind>().notNull(),
  causeActorId: uuid('cause_actor_id').references(() => users.id, { onDelete: 'set null' }),
  transport: text('transport').$type<AgentTurnTransport>().notNull(),
  /** The covered span, half-open: (from, through]. Both stamped at claim. */
  causeFromMessageId: uuid('cause_from_message_id').references(() => messages.id, { onDelete: 'set null' }),
  causeThroughMessageId: uuid('cause_through_message_id').references(() => messages.id, { onDelete: 'set null' }),
  parentTurnId: uuid('parent_turn_id').references((): any => agentTurns.id, { onDelete: 'set null' }),
  depth: integer('depth').default(0).notNull(),
  /** Snapshotted subscription. No default: see the migration for why. */
  deliveryPolicy: text('delivery_policy').$type<AgentTurnDeliveryPolicy>().notNull(),
  deliveryWindowSeconds: integer('delivery_window_seconds'),
  dueAt: timestamp('due_at').defaultNow().notNull(),
  coalesceDeadlineAt: timestamp('coalesce_deadline_at'),
  /** The allowance floor. NULL in the ordinary case. */
  deferredUntil: timestamp('deferred_until'),
  expiresAt: timestamp('expires_at').default(sql`now() + interval '24 hours'`).notNull(),
  posture: text('posture').$type<AgentTurnPosture>().default('restricted').notNull(),
  /** Never an agent; enforced in code because the rule spans tables. */
  payerId: uuid('payer_id').references(() => users.id, { onDelete: 'set null' }),
  priceNanocents: bigint('price_nanocents', { mode: 'bigint' }),
  budgetNanocents: bigint('budget_nanocents', { mode: 'bigint' }),
  /** NULL: the agent's own owner pays. NOT NULL: the token's holder pays. */
  paymentTokenId: uuid('payment_token_id').references(() => paymentTokens.id, { onDelete: 'set null' }),
  state: text('state').$type<AgentTurnState>().default('due').notNull(),
  leaseOwner: text('lease_owner'),
  leaseUntil: timestamp('lease_until'),
  startedAt: timestamp('started_at'),
  finishedAt: timestamp('finished_at'),
  resultMessageId: uuid('result_message_id').references(() => messages.id, { onDelete: 'set null' }),
  error: text('error'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  causeKindCheck: check('agent_turns_cause_kind_check', sql`${table.causeKind} IN ('agent', 'external')`),
  transportCheck: check('agent_turns_transport_check', sql`${table.transport} IN ('portal', 'smtp')`),
  deliveryPolicyCheck: check(
    'agent_turns_delivery_policy_check',
    sql`${table.deliveryPolicy} IN ('immediate', 'debounce', 'interval', 'manual')`,
  ),
  postureCheck: check('agent_turns_posture_check', sql`${table.posture} IN ('full', 'restricted')`),
  stateCheck: check(
    'agent_turns_state_check',
    sql`${table.state} IN ('unfunded', 'due', 'running', 'done', 'failed', 'cancelled', 'expired')`,
  ),
  depthCheck: check('agent_turns_depth_check', sql`${table.depth} >= 0 AND ${table.depth} <= 32`),
  unfundedPriceCheck: check(
    'agent_turns_unfunded_price_check',
    sql`${table.state} <> 'unfunded' OR ${table.priceNanocents} IS NOT NULL`,
  ),
  runningLeaseCheck: check(
    'agent_turns_running_lease_check',
    sql`${table.state} <> 'running' OR (${table.leaseOwner} IS NOT NULL AND ${table.leaseUntil} IS NOT NULL)`,
  ),
  debounceDeadlineCheck: check(
    'agent_turns_debounce_deadline_check',
    sql`${table.deliveryPolicy} <> 'debounce' OR ${table.coalesceDeadlineAt} IS NOT NULL`,
  ),
  deadlineOrderCheck: check(
    'agent_turns_deadline_order_check',
    sql`${table.coalesceDeadlineAt} IS NULL OR ${table.dueAt} <= ${table.coalesceDeadlineAt}`,
  ),
  deferralFloorCheck: check(
    'agent_turns_deferral_floor_check',
    sql`${table.deferredUntil} IS NULL OR ${table.dueAt} >= ${table.deferredUntil}`,
  ),
  // THE central invariant: at most one OPEN turn per (agent, chat).
  openPairIdx: uniqueIndex('agent_turns_open_pair_idx')
    .on(table.chatId, table.agentId)
    .where(sql`${table.state} IN ('unfunded', 'due')`),
  // UNIQUE so the immediate lane can insert a row already 'running' and let
  // the database refuse the concurrent second insert for the same pair.
  runningPairIdx: uniqueIndex('agent_turns_running_pair_idx')
    .on(table.agentId, table.chatId)
    .where(sql`${table.state} = 'running'`),
  watermarkIdx: index('agent_turns_watermark_idx')
    .on(table.agentId, table.chatId, sql`${table.finishedAt} DESC`)
    .where(sql`${table.state} = 'done' AND ${table.causeThroughMessageId} IS NOT NULL`),
  dueIdx: index('agent_turns_due_idx').on(table.dueAt).where(sql`${table.state} = 'due'`),
  leaseIdx: index('agent_turns_lease_idx').on(table.leaseUntil).where(sql`${table.state} = 'running'`),
  // The per-minute expiry sweep's predicate; a two-value IN does not imply dueIdx.
  expiresIdx: index('agent_turns_expires_idx').on(table.expiresAt).where(sql`${table.state} IN ('due', 'unfunded')`),
  unfundedPayerIdx: index('agent_turns_unfunded_payer_idx')
    .on(table.payerId, table.createdAt)
    .where(sql`${table.state} = 'unfunded'`),
  chatCreatedIdx: index('agent_turns_chat_created_idx').on(table.chatId, table.createdAt),
  // Partial on state <> 'done' so a busy agent's archive never stays live in
  // the one per-agent index.
  agentCreatedIdx: index('agent_turns_agent_created_idx')
    .on(table.agentId, table.createdAt)
    .where(sql`${table.state} <> 'done'`),
  finishedIdx: index('agent_turns_finished_idx')
    .on(table.finishedAt)
    .where(sql`${table.state} IN ('done', 'failed', 'cancelled', 'expired')`),
}));

export const agentTurnsRelations = relations(agentTurns, ({ one }) => ({
  chat: one(chats, { fields: [agentTurns.chatId], references: [chats.id] }),
  agent: one(users, { fields: [agentTurns.agentId], references: [users.id] }),
  payer: one(users, { fields: [agentTurns.payerId], references: [users.id] }),
  parent: one(agentTurns, { fields: [agentTurns.parentTurnId], references: [agentTurns.id] }),
  resultMessage: one(messages, { fields: [agentTurns.resultMessageId], references: [messages.id] }),
}));

export type AgentTurn = InferSelectModel<typeof agentTurns>;

/**
 * The append-only log of raises that stored no turn (depth, the loop breaker,
 * no payer, rate, sponsorship, a funding conflict, an exhausted counterparty).
 * Coalesced per (chat, agent, reason) per hour by bumping `count` and
 * `lastAt`; swept seven days after `lastAt`. Its own table because
 * `agent_turns` is a queue whose rows are claimed, and a refusal never is.
 * `reason` carries no CHECK: the vocabulary is `RefusalReason` in
 * lib/turns/types.ts and a new reason must not be a migration.
 */
export const agentTurnRefusals = pgTable('agent_turn_refusals', {
  id: uuid('id').defaultRandom().primaryKey(),
  /** CASCADE, unlike agent_turns: nothing here is priced, so nothing is ledger evidence. */
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'cascade' }).notNull(),
  agentId: uuid('agent_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  causeKind: text('cause_kind').$type<AgentTurnCauseKind>().notNull(),
  causeActorId: uuid('cause_actor_id').references(() => users.id, { onDelete: 'set null' }),
  transport: text('transport').$type<AgentTurnTransport>().notNull(),
  /** The depth the raise computed, so a `depth` refusal records what it refused. */
  depth: integer('depth').default(0).notNull(),
  /** Who would have paid, as resolved at the time; NULL on a no_payer refusal. */
  payerId: uuid('payer_id').references(() => users.id, { onDelete: 'set null' }),
  paymentTokenId: uuid('payment_token_id').references(() => paymentTokens.id, { onDelete: 'set null' }),
  reason: text('reason').notNull(),
  count: integer('count').default(1).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  lastAt: timestamp('last_at').defaultNow().notNull(),
}, (table) => ({
  causeKindCheck: check('agent_turn_refusals_cause_kind_check', sql`${table.causeKind} IN ('agent', 'external')`),
  transportCheck: check('agent_turn_refusals_transport_check', sql`${table.transport} IN ('portal', 'smtp')`),
  depthCheck: check('agent_turn_refusals_depth_check', sql`${table.depth} >= 0 AND ${table.depth} <= 32`),
  countCheck: check('agent_turn_refusals_count_check', sql`${table.count} >= 1`),
  // The coalesce lookup, and the per-chat read (it leads on chat_id).
  coalesceIdx: index('agent_turn_refusals_coalesce_idx')
    .on(table.chatId, table.agentId, table.reason, sql`${table.createdAt} DESC`),
  // The seven-day sweep compares last_at, so a row still being bumped is kept.
  lastIdx: index('agent_turn_refusals_last_idx').on(table.lastAt),
}));

export const agentTurnRefusalsRelations = relations(agentTurnRefusals, ({ one }) => ({
  chat: one(chats, { fields: [agentTurnRefusals.chatId], references: [chats.id] }),
  agent: one(users, { fields: [agentTurnRefusals.agentId], references: [users.id] }),
  payer: one(users, { fields: [agentTurnRefusals.payerId], references: [users.id] }),
}));

export type AgentTurnRefusal = InferSelectModel<typeof agentTurnRefusals>;

export type AgentAddressKind = 'canonical' | 'vanity';
/**
 * `inactive` is the MAILBOX OFF state and is the default a fresh mint lands in
 * (migration 0080). The row still exists, so the unique index still reserves
 * the local part against every other agent; what it does not do is route.
 * `resolveAgentAddress` filters `status = 'active'`, so an inactive address is
 * refused at SMTP and a stranger's mail is never stored. Turning the mailbox on
 * flips inactive -> active; turning it off flips back. Neither is a tombstone:
 * tombstoning frees nothing and is permanent, and using it as an off switch
 * would surrender the local part to the next agent that wants it.
 */
export type AgentAddressStatus = 'active' | 'inactive' | 'tombstoned';
export type AgentAddressTombstoneReason = 'rename' | 'delete' | 'suspension' | 'moderation' | 'reserved';

/**
 * Agent addresses at robutler.io. Deletes here are SOFT: a retired local part
 * keeps its row as a tombstone with agent_id nulled, and the unconditional
 * unique index across every status is what makes reissue impossible. The
 * canonical row stores a value derivable from the agent id and is kept for
 * the tombstone contract; the migration says why and what reopening it costs.
 */
export const agentAddresses = pgTable('agent_addresses', {
  id: uuid('id').defaultRandom().primaryKey(),
  localPart: text('local_part').notNull(),
  domain: text('domain').default('robutler.io').notNull(),
  /** NULL once tombstoned. */
  agentId: uuid('agent_id').references(() => users.id, { onDelete: 'set null' }),
  ownerId: uuid('owner_id').references(() => users.id, { onDelete: 'set null' }),
  kind: text('kind').$type<AgentAddressKind>().notNull(),
  status: text('status').$type<AgentAddressStatus>().default('inactive').notNull(),
  tombstonedAt: timestamp('tombstoned_at'),
  tombstonedReason: text('tombstoned_reason').$type<AgentAddressTombstoneReason>(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  kindCheck: check('agent_addresses_kind_check', sql`${table.kind} IN ('canonical', 'vanity')`),
  statusCheck: check('agent_addresses_status_check', sql`${table.status} IN ('active', 'inactive', 'tombstoned')`),
  tombstonedReasonCheck: check(
    'agent_addresses_tombstoned_reason_check',
    sql`${table.tombstonedReason} IS NULL OR ${table.tombstonedReason} IN ('rename', 'delete', 'suspension', 'moderation', 'reserved')`,
  ),
  /** RFC 5321 local-part ceiling: refuse, never truncate. */
  localLenCheck: check('agent_addresses_local_len', sql`octet_length(${table.localPart}) BETWEEN 1 AND 64`),
  activeHasAgentCheck: check(
    'agent_addresses_active_has_agent',
    sql`${table.status} <> 'active' OR ${table.agentId} IS NOT NULL`,
  ),
  // The permanent tombstone: unconditional across every status.
  localDomainIdx: uniqueIndex('agent_addresses_local_domain_idx').on(table.domain, sql`lower(${table.localPart})`),
  // NOT `status = 'active'`: an off mailbox is `inactive`, and a predicate
  // that excluded it would let one agent accumulate several canonical rows
  // while off, then fail on enable. `<> 'tombstoned'` spans both live states.
  oneCanonicalIdx: uniqueIndex('agent_addresses_one_canonical_idx')
    .on(table.agentId)
    .where(sql`${table.kind} = 'canonical' AND ${table.status} <> 'tombstoned'`),
  oneVanityIdx: uniqueIndex('agent_addresses_one_vanity_idx')
    .on(table.agentId)
    .where(sql`${table.kind} = 'vanity' AND ${table.status} <> 'tombstoned'`),
  ownerIdx: index('agent_addresses_owner_idx').on(table.ownerId),
  // The kind-less active read (`agent_id = ? AND status = 'active'`): neither
  // unique partial above serves it, each carries a kind term the query lacks.
  agentActiveIdx: index('agent_addresses_agent_active_idx').on(table.agentId).where(sql`${table.status} = 'active'`),
}));

export const agentAddressesRelations = relations(agentAddresses, ({ one }) => ({
  agent: one(users, { fields: [agentAddresses.agentId], references: [users.id] }),
  owner: one(users, { fields: [agentAddresses.ownerId], references: [users.id] }),
}));

export type AgentAddress = InferSelectModel<typeof agentAddresses>;

export type InboxContactSenderAuth = 'platform' | 'dmarc_aligned' | 'unauthenticated';
export type InboxContactTransport = 'portal' | 'smtp';
export type InboxContactSecurityVerdict = 'received' | 'unauthenticated' | 'spam' | 'blocked';
/** No `blocked`: a block is a row in `inboxBlocks`, keyed on the owner, not a state of a pair. */
export type InboxContactState = 'pending' | 'accepted' | 'declined' | 'deferred' | 'expired';
export type InboxContactRelevanceVerdict = 'decline' | 'hold' | 'surface';
export type InboxContactRelevanceBy = 'platform' | 'remote';

/**
 * First-contact claim tickets: the RELATIONSHIP between one recipient agent
 * and one sender principal, its state, the newest message's security verdict,
 * the relevance verdict and the exchange's counts. A pending contact is NOT a
 * chat: no chats, messages or chat_participants row exists until a person
 * accepts. The held messages are rows in `inboxContactMessages`, never
 * columns here. Carries no turn counters on purpose: the loop breaker and the
 * totals derive from the materialized chat's messages rows.
 */
export const inboxContacts = pgTable('inbox_contacts', {
  id: uuid('id').defaultRandom().primaryKey(),
  agentId: uuid('agent_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  agentOwnerId: uuid('agent_owner_id').references(() => users.id, { onDelete: 'set null' }),
  /** 'user:<uuid>' | 'mailbox:<mailboxKey>' | 'ip:<rdomain>:<ip24>' */
  senderPrincipal: text('sender_principal').notNull(),
  senderOwnerId: uuid('sender_owner_id').references(() => users.id, { onDelete: 'set null' }),
  /** Normalized, UNTRUSTED. */
  senderLabel: text('sender_label'),
  senderAuth: text('sender_auth').$type<InboxContactSenderAuth>().default('platform').notNull(),
  transport: text('transport').$type<InboxContactTransport>().default('portal').notNull(),
  /** The NEWEST message's verdict; each message carries its own. */
  securityVerdict: text('security_verdict').$type<InboxContactSecurityVerdict>().default('received').notNull(),
  state: text('state').$type<InboxContactState>().default('pending').notNull(),
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'set null' }),
  threadCorrelatorHash: text('thread_correlator_hash'),
  threadCorrelatorExpiresAt: timestamp('thread_correlator_expires_at'),
  /** DMARC-aligned domain recorded at acceptance (SMTP only). */
  threadSenderDomain: text('thread_sender_domain'),
  /** Folded envelope sender at acceptance; reply re-authorization. */
  threadSenderEnvelope: text('thread_sender_envelope'),
  relevanceVerdict: text('relevance_verdict').$type<InboxContactRelevanceVerdict>(),
  relevanceReasonCode: text('relevance_reason_code'),
  relevanceInjectionSuspected: boolean('relevance_injection_suspected').default(false).notNull(),
  /** Attributed agent output, UNTRUSTED-derived. */
  relevanceSummary: text('relevance_summary'),
  relevanceBy: text('relevance_by').$type<InboxContactRelevanceBy>(),
  relevanceAt: timestamp('relevance_at'),
  inboundCount: integer('inbound_count').default(0).notNull(),
  lastOwnerPostAt: timestamp('last_owner_post_at'),
  firstSeenAt: timestamp('first_seen_at').defaultNow().notNull(),
  lastInboundAt: timestamp('last_inbound_at'),
  expiresAt: timestamp('expires_at').default(sql`now() + interval '14 days'`).notNull(),
  deferUntil: timestamp('defer_until'),
  decidedAt: timestamp('decided_at'),
  decidedBy: uuid('decided_by').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  senderAuthCheck: check(
    'inbox_contacts_sender_auth_check',
    sql`${table.senderAuth} IN ('platform', 'dmarc_aligned', 'unauthenticated')`,
  ),
  transportCheck: check('inbox_contacts_transport_check', sql`${table.transport} IN ('portal', 'smtp')`),
  securityVerdictCheck: check(
    'inbox_contacts_security_verdict_check',
    sql`${table.securityVerdict} IN ('received', 'unauthenticated', 'spam', 'blocked')`,
  ),
  stateCheck: check(
    'inbox_contacts_state_check',
    sql`${table.state} IN ('pending', 'accepted', 'declined', 'deferred', 'expired')`,
  ),
  relevanceVerdictCheck: check(
    'inbox_contacts_relevance_verdict_check',
    sql`${table.relevanceVerdict} IN ('decline', 'hold', 'surface')`,
  ),
  relevanceByCheck: check(
    'inbox_contacts_relevance_by_check',
    sql`${table.relevanceBy} IS NULL OR ${table.relevanceBy} IN ('platform', 'remote')`,
  ),
  acceptedCheck: check(
    'inbox_contacts_accepted_check',
    sql`${table.state} <> 'accepted' OR (${table.chatId} IS NOT NULL AND ${table.decidedAt} IS NOT NULL)`,
  ),
  decidedCheck: check(
    'inbox_contacts_decided_check',
    sql`${table.state} IN ('pending', 'expired') OR ${table.decidedAt} IS NOT NULL`,
  ),
  /** An unauthenticated sender can never inherit an accepted pair. */
  unauthNoThreadCheck: check(
    'inbox_contacts_unauth_no_thread',
    sql`${table.senderAuth} <> 'unauthenticated' OR ${table.state} <> 'accepted'`,
  ),
  // One row per (recipient agent, sender principal), unconditional across
  // every state: the per-pair cap and the acceptance race guard.
  pairIdx: uniqueIndex('inbox_contacts_pair_idx').on(table.agentId, table.senderPrincipal),
  drainIdx: index('inbox_contacts_drain_idx')
    .on(table.agentId, table.lastInboundAt)
    .where(sql`${table.state} = 'pending' AND ${table.relevanceAt} IS NULL AND ${table.securityVerdict} IN ('received', 'unauthenticated')`),
  threadIdx: index('inbox_contacts_thread_idx')
    .on(table.agentId, table.lastInboundAt)
    .where(sql`${table.state} = 'accepted'`),
  senderOwnerIdx: index('inbox_contacts_sender_owner_idx').on(table.senderOwnerId, table.firstSeenAt),
  // The clock and the retention purge (lib/inbox/retention.ts): one index per
  // predicate, keyed on the column that predicate compares.
  expiryIdx: index('inbox_contacts_expiry_idx').on(table.expiresAt).where(sql`${table.state} = 'pending'`),
  deferredIdx: index('inbox_contacts_deferred_idx').on(table.deferUntil).where(sql`${table.state} = 'deferred'`),
  declinedIdx: index('inbox_contacts_declined_idx').on(table.decidedAt).where(sql`${table.state} = 'declined'`),
  // Expression index: the purge compares exactly coalesce(defer_until, expires_at).
  expiredIdx: index('inbox_contacts_expired_idx')
    .on(sql`coalesce(${table.deferUntil}, ${table.expiresAt})`)
    .where(sql`${table.state} = 'expired'`),
  correlatorIdx: uniqueIndex('inbox_contacts_correlator_idx')
    .on(table.threadCorrelatorHash)
    .where(sql`${table.threadCorrelatorHash} IS NOT NULL`),
}));

export const inboxContactsRelations = relations(inboxContacts, ({ one, many }) => ({
  agent: one(users, { fields: [inboxContacts.agentId], references: [users.id] }),
  chat: one(chats, { fields: [inboxContacts.chatId], references: [chats.id] }),
  messages: many(inboxContactMessages),
}));

export type InboxContact = InferSelectModel<typeof inboxContacts>;

/**
 * The messages a pending pair is holding, one row each, normalised and
 * clamped to 8 KB. A second message APPENDS; the retry guard (the unique
 * partial index on contact and Message-ID plus ON CONFLICT DO NOTHING) holds
 * for every message on the pair. Dropping the body on decline, block or
 * expiry is a DELETE of these rows; the account purge reaches them through
 * the CASCADE off inbox_contacts. The verdict and the check JSON are about
 * ONE message's bytes and so ride here.
 */
export const inboxContactMessages = pgTable('inbox_contact_messages', {
  id: uuid('id').defaultRandom().primaryKey(),
  contactId: uuid('contact_id').references(() => inboxContacts.id, { onDelete: 'cascade' }).notNull(),
  /** The RFC 5322 Message-ID, folded; NULL on the portal rail and on mail without one. */
  externalMessageId: text('external_message_id'),
  dedupHash: text('dedup_hash'),
  subject: text('subject'),
  body: text('body').notNull(),
  securityVerdict: text('security_verdict').$type<InboxContactSecurityVerdict>().default('received').notNull(),
  /** Check ids and trusted integers; NEVER content. */
  securityChecks: jsonb('security_checks').$type<Record<string, unknown> | null>(),
  strippedHiddenChars: integer('stripped_hidden_chars').default(0).notNull(),
  truncated: boolean('truncated').default(false).notNull(),
  attachmentCount: integer('attachment_count').default(0).notNull(),
  byteSize: integer('byte_size').default(0).notNull(),
  receivedAt: timestamp('received_at').defaultNow().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  securityVerdictCheck: check(
    'inbox_contact_messages_security_verdict_check',
    sql`${table.securityVerdict} IN ('received', 'unauthenticated', 'spam', 'blocked')`,
  ),
  /** The database-side body cap: 8 KB of octets, whatever the caller believed. */
  bodyCapCheck: check('inbox_contact_messages_body_cap', sql`octet_length(${table.body}) <= 8192`),
  contactIdx: index('inbox_contact_messages_contact_idx').on(table.contactId, table.receivedAt),
  // The retry guard: the same Message-ID on the same pair is one row.
  externalMsgIdx: uniqueIndex('inbox_contact_messages_external_msg_idx')
    .on(table.contactId, table.externalMessageId)
    .where(sql`${table.externalMessageId} IS NOT NULL`),
}));

export const inboxContactMessagesRelations = relations(inboxContactMessages, ({ one }) => ({
  contact: one(inboxContacts, { fields: [inboxContactMessages.contactId], references: [inboxContacts.id] }),
}));

export type InboxContactMessage = InferSelectModel<typeof inboxContactMessages>;

/**
 * The owner's block list: one row per (owner, sender principal). Belongs to
 * the PERSON, covers every agent they own, can be written before the stranger
 * has ever written, and is never swept. Never a bucket-only `ip:` principal
 * (a permanent block on a /24 is a block on everyone behind a NAT); the CHECK
 * makes that a database fact. Consulted by admit() before the pair lookup.
 */
/**
 * The outbound relay's OWN suppression list (migration 0081).
 *
 * A SECOND TABLE, not a column on `email_suppressions`, and the separation is
 * the entire point of D-16. That table keys on `email` alone, so one address
 * is one row for the whole platform: a single `spamreport` about an agent's
 * reply would make `shouldSuppress` (lib/email/policy.ts) return true for
 * `money` and `legal` too, and quietly stop that person's payout receipts and
 * password resets. An isolated SendGrid key does not prevent that. Only an
 * isolated list does.
 *
 * The isolation is STRUCTURAL. `lib/email/service.ts` does not import this
 * table and cannot read it, so a relay complaint has no path to a transactional
 * decision even if somebody later forgets a predicate.
 */
export const inboxRelaySuppressions = pgTable('inbox_relay_suppressions', {
  email: text('email').primaryKey(),
  /** No 'manual': this rail has no operator surface. Declining the pair is that control. */
  reason: text('reason').$type<InboxRelaySuppressionReason>().notNull(),
  note: text('note'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  reasonCheck: check(
    'inbox_relay_suppressions_reason_check',
    sql`${table.reason} IN ('complaint', 'hard_bounce', 'unsubscribed')`,
  ),
  noteLenCheck: check(
    'inbox_relay_suppressions_note_len',
    sql`${table.note} IS NULL OR octet_length(${table.note}) <= 500`,
  ),
  // `sql` rather than drizzle's `desc()`, which this file does not import; the
  // 0081 SQL spells the same DESC and a drizzle-kit diff must stay empty.
  createdIdx: index('inbox_relay_suppressions_created_idx').on(sql`${table.createdAt} DESC`),
}));

export const inboxBlocks = pgTable('inbox_blocks', {
  id: uuid('id').defaultRandom().primaryKey(),
  ownerId: uuid('owner_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  senderPrincipal: text('sender_principal').notNull(),
  /** The owner's or the operator's note; capped, never shown to the sender. */
  reason: text('reason'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  noBucketPrincipalCheck: check('inbox_blocks_no_bucket_principal', sql`${table.senderPrincipal} NOT LIKE 'ip:%'`),
  reasonLenCheck: check('inbox_blocks_reason_len', sql`${table.reason} IS NULL OR octet_length(${table.reason}) <= 200`),
  // One block per (owner, principal); it is also the admission lookup.
  pairIdx: uniqueIndex('inbox_blocks_pair_idx').on(table.ownerId, table.senderPrincipal),
}));

export const inboxBlocksRelations = relations(inboxBlocks, ({ one }) => ({
  owner: one(users, { fields: [inboxBlocks.ownerId], references: [users.id] }),
}));

export type InboxBlock = InferSelectModel<typeof inboxBlocks>;

// ── Resumable uploads (ADR-0059 lane A2, migration 0097) ─────────────────────
// One row per upload in flight: the declared size and SHA-256 (and chunk
// list) the bytes must match before a content row is ever written, where the
// bytes are going (a staged temp file on the store's mount, or a staging
// object in the bucket written by the client straight through a GCS
// resumable session), how far they got, and the options the final content
// row is created with. A row ends 'committed' (content_id set), 'failed' (the
// error names the chunk or the hash) or 'aborted'. Rows are small; the bytes
// are never in the database.
export const contentUploads = pgTable('content_uploads', {
  id: uuid('id').defaultRandom().primaryKey(),
  /** The content row's future creator (after an admin's on-behalf choice). */
  ownerId: uuid('owner_id').references(() => users.id, { onDelete: 'cascade' }).notNull(),
  /** Who authenticated the create call; the only principal allowed to drive the upload. */
  createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
  /** 'local' (tus to a staged temp file) or 'gcs' (direct resumable session to the bucket). */
  mode: text('mode').notNull(),
  status: text('status').notNull().default('open'),
  originalName: text('original_name').notNull(),
  mimeType: text('mime_type').notNull(),
  contentType: contentTypeEnum('content_type').notNull(),
  size: bigint('size', { mode: 'bigint' }).notNull(),
  sha256: text('sha256').notNull(),
  chunkSize: integer('chunk_size'),
  /** jsonb array of lower-case hex SHA-256, one per chunk; null when the client sent no list. */
  chunkHashes: jsonb('chunk_hashes'),
  /** Bytes committed so far (local: verified chunks on disk; gcs: what the client reported). */
  offsetBytes: bigint('offset_bytes', { mode: 'bigint' }).notNull().default(BigInt(0)),
  /** local: the store-relative pointer of the staged file; gcs: the staging object's name. */
  stagingPath: text('staging_path'),
  gcsSessionUri: text('gcs_session_uri'),
  /** visibility, chatId, parentId, displayName, sourcePostId: the save options. */
  options: jsonb('options').notNull().default({}),
  contentId: uuid('content_id').references(() => content.id, { onDelete: 'set null' }),
  error: text('error'),
  expiresAt: timestamp('expires_at').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  modeCheck: check('content_uploads_mode_check', sql`${table.mode} IN ('local', 'gcs')`),
  statusCheck: check('content_uploads_status_check',
    sql`${table.status} IN ('open', 'uploaded', 'verifying', 'committed', 'failed', 'aborted')`),
  sha256Check: check('content_uploads_sha256_check', sql`${table.sha256} ~ '^[0-9a-f]{64}$'`),
  // The per-account open-upload count and the sweep of stale rows.
  ownerStatusIdx: index('content_uploads_owner_status_idx').on(table.ownerId, table.status),
  statusExpiresIdx: index('content_uploads_status_expires_idx').on(table.status, table.expiresAt),
}));

export const contentUploadsRelations = relations(contentUploads, ({ one }) => ({
  owner: one(users, { fields: [contentUploads.ownerId], references: [users.id] }),
  content: one(content, { fields: [contentUploads.contentId], references: [content.id] }),
}));

export type ContentUpload = InferSelectModel<typeof contentUploads>;

// ── Artifact index (ADR-0059 lane A4, migration 0098) ─────────────────────────
// The pull-through cache by hash behind GET /api/blob/cas/<sha256> and the
// relay need to know, from a hash alone, which manifest names it, where its
// bytes can be fetched and how long a cached copy is kept
// (lib/artifacts/index-store.ts says the rules). One row per manifest the
// platform was told about, one row per object (file, chunk, nested manifest)
// the manifest names. A manifest is PINNED while a content row's storage_path
// is its own CAS pointer: a file in a folder. Rows are small; bytes are in the
// store, never here.
export const artifactManifests = pgTable('artifact_manifests', {
  /** SHA-256 of the canonical (RFC 8785) manifest bytes: the identity. */
  sha256: text('sha256').primaryKey(),
  size: bigint('size', { mode: 'bigint' }).notNull(),
  /** CAS pointer of the canonical bytes once this platform holds them. */
  pointer: text('pointer'),
  producerAgent: text('producer_agent').notNull(),
  /** The agent (users.id) that announced the set over its connection, if any. */
  producerUserId: uuid('producer_user_id').references(() => users.id, { onDelete: 'set null' }),
  taskId: text('task_id'),
  /** The chat the announcing session was bound to: the delegating caller's room. */
  chatId: uuid('chat_id').references(() => chats.id, { onDelete: 'set null' }),
  /** jsonb array of the set-level locations (announced first, then the manifest's). */
  locations: jsonb('locations').notNull().default([]),
  /** The producer's expiresAt, else announce time plus ARTIFACT_CACHE_TTL_HOURS. */
  expiresAt: timestamp('expires_at').notNull(),
  indexedAt: timestamp('indexed_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  sha256Check: check('artifact_manifests_sha256_check', sql`${table.sha256} ~ '^[0-9a-f]{64}$'`),
  expiresIdx: index('artifact_manifests_expires_idx').on(table.expiresAt),
  producerUserIdx: index('artifact_manifests_producer_user_idx').on(table.producerUserId),
}));

export const artifactBlobs = pgTable('artifact_blobs', {
  id: uuid('id').defaultRandom().primaryKey(),
  /** The object's own hash: a file's, a chunk's, or a nested manifest's. */
  sha256: text('sha256').notNull(),
  manifestSha256: text('manifest_sha256').references(() => artifactManifests.sha256, { onDelete: 'cascade' }).notNull(),
  kind: text('kind').notNull(),
  entryPath: text('entry_path').notNull(),
  size: bigint('size', { mode: 'bigint' }).notNull(),
  /** For a chunk: the hash of the file it belongs to. */
  parentSha256: text('parent_sha256'),
  chunkIndex: integer('chunk_index'),
  offsetBytes: bigint('offset_bytes', { mode: 'bigint' }).notNull().default(BigInt(0)),
  mediaType: text('media_type').notNull(),
  chunkSize: integer('chunk_size'),
  /** jsonb array of lower-case hex SHA-256, one per chunk, for a chunked file row. */
  chunkHashes: jsonb('chunk_hashes'),
  /** jsonb array of the locations serving this object's file. */
  locations: jsonb('locations').notNull().default([]),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  kindCheck: check('artifact_blobs_kind_check', sql`${table.kind} IN ('file', 'chunk', 'manifest')`),
  sha256Check: check('artifact_blobs_sha256_check', sql`${table.sha256} ~ '^[0-9a-f]{64}$'`),
  // The cache's lookup: a hash to the rows naming it.
  sha256Idx: index('artifact_blobs_sha256_idx').on(table.sha256),
  manifestIdx: index('artifact_blobs_manifest_idx').on(table.manifestSha256),
}));

export const artifactBlobsRelations = relations(artifactBlobs, ({ one }) => ({
  manifest: one(artifactManifests, { fields: [artifactBlobs.manifestSha256], references: [artifactManifests.sha256] }),
}));

export type ArtifactManifestRow = InferSelectModel<typeof artifactManifests>;
export type ArtifactBlobRow = InferSelectModel<typeof artifactBlobs>;

// ── CASFS (ADR 0063, migration 0099) ──────────────────────────────────────────
// A project's data as a Merkle tree of `casfs.dir/1` manifests over CAS blobs
// (lib/casfs/). The head moves only by compare-and-swap (`counter` is the
// swap's witness); `casfs_refs` lists every (sha, path) the head and the kept
// versions name, which is both the blob route's membership test (a hash is
// never a capability) and the GC's keep set. Bytes are in the store, never here.
export const casfsProjects = pgTable('casfs_projects', {
  projectId: uuid('project_id').primaryKey().references(() => content.id, { onDelete: 'cascade' }),
  head: text('head'),
  counter: bigint('counter', { mode: 'number' }).notNull().default(0),
  writerId: uuid('writer_id').references(() => users.id, { onDelete: 'set null' }),
  payerId: uuid('payer_id').references(() => users.id, { onDelete: 'set null' }),
  platformBytes: bigint('platform_bytes', { mode: 'number' }).notNull().default(0),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  headCheck: check('casfs_projects_head_check', sql`${table.head} IS NULL OR ${table.head} ~ '^[0-9a-f]{64}$'`),
}));

export const casfsVersions = pgTable('casfs_versions', {
  id: uuid('id').defaultRandom().primaryKey(),
  projectId: uuid('project_id').notNull().references(() => casfsProjects.projectId, { onDelete: 'cascade' }),
  root: text('root').notNull(),
  parent: text('parent'),
  counter: bigint('counter', { mode: 'number' }).notNull(),
  message: text('message'),
  label: text('label'),
  kept: boolean('kept').notNull().default(false),
  authorId: uuid('author_id').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => ({
  rootCheck: check('casfs_versions_root_check', sql`${table.root} ~ '^[0-9a-f]{64}$'`),
  parentCheck: check('casfs_versions_parent_check', sql`${table.parent} IS NULL OR ${table.parent} ~ '^[0-9a-f]{64}$'`),
  projectIdx: index('casfs_versions_project_idx').on(table.projectId, table.counter),
  rootIdx: index('casfs_versions_root_idx').on(table.projectId, table.root),
}));

export const casfsRefs = pgTable('casfs_refs', {
  projectId: uuid('project_id').notNull().references(() => casfsProjects.projectId, { onDelete: 'cascade' }),
  root: text('root').notNull(),
  sha256: text('sha256').notNull(),
  path: text('path').notNull(),
  kind: text('kind').notNull(),
  policy: text('policy').notNull(),
  size: bigint('size', { mode: 'number' }).notNull(),
}, (table) => ({
  pk: primaryKey({ name: 'casfs_refs_pk', columns: [table.projectId, table.root, table.kind, table.path, table.sha256] }),
  kindCheck: check('casfs_refs_kind_check', sql`${table.kind} IN ('dir', 'file', 'index', 'chunk', 'sealed', 'mount')`),
  policyCheck: check('casfs_refs_policy_check', sql`${table.policy} IN ('cas', 'both', 'local-served', 'local-only')`),
  sha256Check: check('casfs_refs_sha256_check', sql`${table.sha256} ~ '^[0-9a-f]{64}$'`),
  shaIdx: index('casfs_refs_sha_idx').on(table.projectId, table.sha256),
  globalShaIdx: index('casfs_refs_global_sha_idx').on(table.sha256),
  mountPinIdx: index('casfs_refs_mount_pin_idx').on(table.sha256).where(sql`${table.kind} = 'mount'`),
}));

export const casfsSealKeys = pgTable('casfs_seal_keys', {
  id: uuid('id').defaultRandom().primaryKey(),
  projectId: uuid('project_id').notNull().references(() => casfsProjects.projectId, { onDelete: 'cascade' }),
  keyId: text('key_id').notNull(),
  memberId: uuid('member_id').notNull().references(() => users.id, { onDelete: 'cascade' }),
  deviceId: text('device_id').notNull(),
  devicePub: text('device_pub').notNull(),
  wrapped: text('wrapped').notNull(),
  createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  revokedAt: timestamp('revoked_at'),
}, (table) => ({
  keyIdCheck: check('casfs_seal_keys_key_id_check', sql`${table.keyId} ~ '^[0-9a-f]{16,64}$'`),
  projectIdx: index('casfs_seal_keys_project_idx').on(table.projectId, table.keyId),
  memberIdx: index('casfs_seal_keys_member_idx').on(table.memberId),
}));

export const casfsGitLinks = pgTable('casfs_git_links', {
  projectId: uuid('project_id').primaryKey().references(() => casfsProjects.projectId, { onDelete: 'cascade' }),
  provider: text('provider').notNull(),
  repo: text('repo').notNull(),
  branch: text('branch').notNull().default('main'),
  mode: text('mode').notNull().default('every-save'),
  intervalMinutes: integer('interval_minutes'),
  linkedBy: uuid('linked_by').references(() => users.id, { onDelete: 'set null' }),
  /** GitHub: the GitHub App installation that covers this one repository. */
  installationId: text('installation_id'),
  /** GitLab: the linking person's connected account (token encrypted there, like every connector secret). */
  credentialAccountId: uuid('credential_account_id').references(() => connectedAccounts.id, { onDelete: 'set null' }),
  status: text('status').notNull().default('never'),
  lastPushedRoot: text('last_pushed_root'),
  lastCommitOid: text('last_commit_oid'),
  lastPushAt: timestamp('last_push_at'),
  lastError: text('last_error'),
  lastErrorAt: timestamp('last_error_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => ({
  providerCheck: check('casfs_git_links_provider_check', sql`${table.provider} IN ('github', 'gitlab')`),
  modeCheck: check('casfs_git_links_mode_check', sql`${table.mode} IN ('every-save', 'schedule', 'manual')`),
  statusCheck: check('casfs_git_links_status_check', sql`${table.status} IN ('never', 'ok', 'failing', 'pushing')`),
}));
